// SPDX-License-Identifier: Apache-2.0
// Copyright 2026 The Zyra Project

/**
 * Main application entry point
 *
 * Load a dataset via URL query param: ?dataset=INTERNAL_SOS_768
 * No dataset param = just the default Earth globe
 */

import { MapRenderer } from './services/mapRenderer'
import { ViewportManager, type ViewLayout } from './services/viewportManager'

// CSS entry point — all component styles imported in dependency order.
// Vite bundles these into a single <link> in the production build.
import './styles/index.css'

import { HLSService } from './services/hlsService'
import { dataService, PreviewFetchError } from './services/dataService'
import { formatDate, videoTimeToDate, dateToVideoTime, computeSiblingSyncCorrection, SIBLING_MIN_READY_STATE, SIBLING_HARD_SEEK_THRESHOLD_S, SIBLING_SEEK_EPS_S, verifySiblingTime, shownFrameTime, isSubDailyPeriod, getSunPosition, inferDisplayInterval } from './utils/time'
import { logger } from './utils/logger'
import type { AppState, VideoTextureHandle, TourFile, Dataset } from './types'

// Extracted modules
import { showBrowseUI, hideBrowseUI, collapseBrowseUI, notifyBrowseOpened, refreshBrowseNewSinceBadge } from './ui/browseUI'
import { initDownloadUI } from './ui/downloadUI'
import { initDownloadDialogUI } from './ui/downloadDialogUI'
import { initPlaylistUI } from './ui/playlistUI'
import {
  getActive as getActivePlaylistPlayback,
  initPlaylistPlayback,
  notifyTourEnded as notifyPlaylistTourEnded,
  onPlaybackChange as onPlaylistPlaybackChange,
  skipNext as skipNextPlaylistEntry,
} from './services/playlistPlayback'
import { updateMapControlsPosition } from './ui/mapControlsUI'
import { initToolsMenu, syncToolsMenuState, syncToolsMenuLayout, pulseBrowseButton } from './ui/toolsMenuUI'
import { closeOutputUI, initOutputUI, openOutputUI } from './ui/outputUI'
import { openCreditsPanel } from './ui/creditsPanel'
import { initChatUI, openChat, openChatSettings, notifyDatasetChanged, showChatTrigger, hideChatTrigger, closeChat, flushPendingGlobeActions, isOrbitAvailable, setOrbitAvailable } from './ui/chatUI'
import { startNodeFeaturesRead, watchForOrbitWithdrawal, type PendingNodeFeatures } from './services/nodeFeatures'
import { SPLASH_TAGLINE_WAIT_MS, bindSplashTagline } from './ui/splashTagline'
import { bindBrowseSubtitle } from './ui/browseSubtitle'
import { loadViewPreferences, saveViewPreferences, type ViewPreferences } from './utils/viewPreferences'
import { renderColorbar, openDisplayControls, closeDisplayControls } from './ui/colorbarUI'
import {
  initAnalyzeUI,
  openAnalyzeUI,
  closeAnalyzeUI,
  notifyAnalyzeDatasetChanged,
  notifyAnalyzePlaybackSettled,
} from './ui/analyzeUI'
import { createPlaybackSettleWatcher } from './services/playbackSettle'
import { registerAnalysisSource } from './services/docentAnalysisTools'
import { buildHistogram } from './services/datasetStats'
import { DEFAULT_DISPLAY, type ColorScaleDisplay } from './services/colorScaleDisplay'
import { RENDER_ENCODING_DATA_LUMA } from './types/color-scale'
import { initHelpUI, setActiveDataset as setHelpActiveDataset } from './ui/helpUI'
import { showDisclosureBannerIfNeeded } from './ui/disclosureBanner'
import {
  createFetchTransport,
  emit,
  initSession,
  pauseForVrEntry,
  resumeForVrExit,
  setTransport,
  startPerfSampler,
  TELEMETRY_BUILD_ENABLED,
  TELEMETRY_CONSOLE_MODE,
} from './analytics'
import {
  createPlaybackState, startPlaybackLoop, stopPlaybackLoop,
  togglePlayPause, rewind, fastForward, stepFrame, onScrub,
  updatePlayButton, toggleCaptions, resetPlaybackState, initPlaybackPositioning,
  seekToDate,
  checkSeekToDate,
  type PlaybackState,
} from './ui/playbackController'
import {
  loadImageDataset, loadVideoDataset, displayDatasetInfo,
  type EventNavResult,
} from './services/datasetLoader'
import { resolveRegion } from './data/regions'
import type { PublicEvent } from './services/eventsService'
import { TourEngine, type TourTelemetryMeta } from './services/tourEngine'
import { showTourControls, hideTourControls, hideAllTourTextBoxes, hideAllTourImages, hideAllTourVideos, hideAllTourPopups, hideAllTourQuestions } from './ui/tourUI'
import { initLegendForDataset, clearLegendCache, loadConfig, readCurrentTime } from './services/docentService'
import { isMobile, IS_MOBILE_NATIVE, getCloudTextureUrl } from './utils/deviceCapability'
import { hasWebGL2 } from './utils/webglSupport'
import { isPublisherPortalPath } from './utils/publisherPath'
import { initDeepLinks } from './services/deepLinkService'
import {
  buildDatasetPath,
  buildNoDatasetPath,
  isDatasetRef,
  parseDatasetPathname,
  previewDatasetRef,
} from './utils/datasetUrl'
import { recordVisit, writeLastSession } from './services/visitMemory'
import { getCatalogMode, setCatalogMode } from './utils/catalogMode'
import { applyEmbedMode } from './utils/embedMode'
import {
  hideCatalogTabs,
  initCatalogTabs,
  setActiveCatalogTab,
  showCatalogTabs,
} from './ui/catalogTabsUI'
import {
  applyPosterDeepLinks,
  parseInitialLayout,
} from './utils/posterDeepLinks'
import { initVrButton } from './ui/vrButton'
import { flyToOnGlobe, isVrActive } from './services/vrSession'
import type { VrDatasetTexture } from './services/vrScene'
import { overlayOptionsFromDataset } from './services/datasetOverlayOptions'
import { publishGlobeState } from './services/multiOutput/globeStateEvents'
import {
  displayForMirror,
  operatorCameraFrom,
  panelMirrorState,
  playbackFrom,
  primaryFrom,
  sharedViewFrom,
  toMirroredDataset,
} from './services/multiOutput/mirrorState'
import {
  startMultiOutput,
  type MultiOutputBootHandle,
} from './services/multiOutput/bootMultiOutput'
import {
  createFullscreenController,
  createIdleCursor,
  createQuitHotkey,
  resolveChromeHost,
  restoreOnLaunch,
  type FullscreenController,
  type IdleCursor,
} from './services/windowChrome'
import { resolveFrameQuery } from './utils/frames'
import { initTourAuthoring } from './ui/tourAuthoring'
import { bootstrapI18n } from './i18n/bootstrap'
import { formatProbeReading } from './services/datasetProbe'
import { initUiScale } from './services/uiScaleService'
import { initShaderSettings } from './services/shaderSettingsService'
import { maybeInitShaderTuner } from './ui/shaderTunerUI'

// Apply the persisted UI-scale to `:root` at module-evaluation
// time — before DOMContentLoaded, before any UI renders, before
// the loading screen's first paint cycle completes. Waiting for
// the DOMContentLoaded handler would let the loading screen
// paint once at `--ui-scale: 1` and then snap to the user's
// preset, producing a visible flash on every reload for visitors
// who pick a non-default size. Module-eval is the earliest hook
// the bundle gives us short of an inline <script> in <head>.
// SSR-safe (the service no-ops when `document` is undefined).
// See docs/WEB_CATALOG_FEATURES_PLAN.md §7.1.
initUiScale()

// Apply persisted shader settings (§7.2) — currently the specular-
// strength preset. Contrast / saturation / bump are dev-tuned and
// ship via SHADER_DEFAULTS. The earth-tile-layer + photoreal Earth
// read getShaderSettings() per-frame, so this just primes the
// snapshot.
initShaderSettings()

// Mount the §7.2 shader-tuner panel — no-op unless the URL carries
// `?tune=shader`. Dev surface for picking the shipped SHADER_DEFAULTS
// values; never reaches end-users via the Tools menu.
maybeInitShaderTuner()

// Phase 5: set a body class so CSS can target mobile-native adaptations
// (larger touch targets, bottom sheets, etc.) without JS per-component.
if (IS_MOBILE_NATIVE) {
  document.body.classList.add('mobile-native')
}

// --- App constants ---
const CLOUD_TEXTURE_URL = getCloudTextureUrl()
const EARTH_TEXTURE_WEIGHT = 0.8
const CLOUD_TEXTURE_WEIGHT = 0.2
const LOADING_BASE_PROGRESS = 20
const LOADING_TEXTURE_RANGE = 70
const LOADING_HIDE_DELAY_MS = 300

/**
 * How long to let a requested redraw land before a panel that is showing
 * the wrong frame is reported as such.
 *
 * `needsUpdate` schedules a repaint, so the upload takes a frame or two.
 * Generous next to that, and short enough to be imperceptible against
 * the settle delay that precedes it.
 */
const SIBLING_REPAIR_CONFIRM_MS = 120

/**
 * Root application class that boots the WebGL globe, loads datasets,
 * and orchestrates all UI subsystems (browse panel, chat, playback controls).
 *
 * Lifecycle: constructed once on DOMContentLoaded, then {@link initialize}
 * fetches the dataset catalog and either displays the default Earth or
 * loads a dataset specified by the `?dataset=` URL parameter.
 */
/**
 * Per-panel dataset + video state. Length is kept in sync with the
 * viewport count by onLayoutChange callbacks from ViewportManager.
 * Slot 0 is always the first panel; the primary index (which slot
 * drives the playback transport UI) lives inside ViewportManager.
 */
interface PanelState {
  dataset: Dataset | null
  hlsService: HLSService | null
  videoTexture: VideoTextureHandle | null
  /** Decoded image element for image datasets — passed to VR to avoid re-fetching. */
  image: HTMLImageElement | null
  /** Wall-clock `Date.now()` at which the current dataset finished
   * loading into this panel. Used to compute `layer_unloaded.dwell_ms`.
   * Null when the panel is empty (default Earth). */
  loadedAt: number | null
  /**
   * Which dataset the panel's `image` / `hlsService` actually belongs to.
   *
   * Not the same question as `dataset`, which is assigned *before* the
   * load is attempted and stays set when one fails, when one is still in
   * flight, and for a `tour/json` row that never paints anything. The
   * two disagreeing is what lets a mirrored frame carry one dataset's
   * identity over another's pixels, so anything describing what is on
   * screen must compare them rather than trusting `dataset` alone.
   */
  mediaDatasetId: string | null
}

function createPanelState(): PanelState {
  return {
    dataset: null,
    hlsService: null,
    videoTexture: null,
    image: null,
    loadedAt: null,
    mediaDatasetId: null,
  }
}

/** Map a dataset-load trigger to the analytics tour-source enum.
 * `tour_started.source` accepts `'browse' | 'orbit' | 'deeplink'`;
 * URL/deep-link loads collapse to `'deeplink'`, defaults / chained
 * loads collapse to `'browse'`. */
function triggerToTourSource(
  trigger: import('./types').LoadTrigger,
): 'browse' | 'orbit' | 'deeplink' {
  if (trigger === 'orbit') return 'orbit'
  if (trigger === 'url') return 'deeplink'
  return 'browse'
}

/**
 * Map a `PreviewFetchError` code to a user-facing message for the
 * boot-time error overlay. The codes mirror the typed envelopes on
 * the server preview endpoints (`functions/api/v1/datasets/[id]/
 * preview/[token].ts` and its `/manifest` sibling). Anything we
 * don't recognise falls through to a generic message so a new code
 * landing server-side doesn't surface as an empty banner.
 */
/**
 * Phase 3pg/C — derive an image MIME type from a frame URL's
 * extension. The server allows `png` / `jpg` / `jpeg` / `webp`
 * frame sequences (per `buildFrameKey`'s `[a-z0-9]+` extension
 * rule), so the synthetic single-frame dataset built by
 * `loadFrameFromChat` needs a `format` that matches the actual
 * bytes the URL points at. Falls back to `image/png` for
 * unknown extensions — same shape the per-frame HEAD handler
 * uses for the `Content-Type` header.
 */
function mimeForFrameUrl(url: string): string {
  const ext = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(url)?.[1]?.toLowerCase()
  switch (ext) {
    case 'png':
      return 'image/png'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'webp':
      return 'image/webp'
    default:
      return 'image/png'
  }
}

function previewErrorMessage(code: string): string {
  switch (code) {
    case 'invalid_token':
      return 'This preview link is invalid or has expired. Ask the publisher to mint a new one.'
    case 'token_id_mismatch':
      return 'This preview link is for a different dataset. Ask the publisher to mint a new one.'
    case 'not_found':
      return 'The dataset this preview points at no longer exists.'
    case 'preview_unconfigured':
      return 'Preview links are not configured on this deployment.'
    case 'binding_missing':
    case 'identity_missing':
      return 'The catalog backend is unavailable. Try again in a moment.'
    default:
      return 'Could not load this preview.'
  }
}

class InteractiveSphere {
  private appState: AppState = {
    datasets: [],
    currentDataset: null,
    isLoading: false,
    error: null,
    timeLabel: '--',
    isPlaying: false,
    currentFrame: 0,
    totalFrames: 0
  }

  private readonly isMobile = isMobile()

  private viewports: ViewportManager = new ViewportManager()

  /** Per-panel dataset state (one entry per active viewport). */
  private panelStates: PanelState[] = []

  /** Listener attached to the primary video for sibling sync. */
  private primaryVideoSyncListeners: Array<{ event: string; handler: EventListener }> = []
  private primaryVideoSyncTarget: HTMLVideoElement | null = null
  /**
   * True while sibling sync is wired to a primary video. Gates the
   * per-frame drift correction driven off the playback rAF loop — see
   * `correctSiblingDrift()`.
   */
  private primaryVideoSyncActive = false
  /**
   * Per-slot record of whether a sibling was frozen out-of-range on the
   * previous drift-correction frame. Lets `correctSiblingDrift` resume a
   * sibling with a single `play()` on the out→in transition instead of
   * re-issuing `play()` (and allocating/catching a Promise) every frame
   * when autoplay is blocked or a stream is briefly unplayable.
   */
  private siblingOutOfRange: boolean[] = []
  /**
   * The primary's true instant behind the shared time label, or `null`
   * when the label is asserting nothing. Stashed by
   * `updateVideoTimeLabel` as it formats, so `verifySiblingTimes` checks
   * panels against the position actually on screen rather than
   * re-deriving one that could differ. Unsnapped on purpose — see
   * `verifySiblingTime`.
   */
  private assertedLabelDate: Date | null = null
  /** Per-slot record of which siblings currently carry a time notice,
   *  so clearing them while playing costs one array scan and no DOM. */
  private siblingTimeNoticed: boolean[] = []
  /**
   * Per-slot abort handle for an armed `seeked`-driven texture upload.
   *
   * Non-null means one is already armed, so a second seek in the same
   * frame does not stack another listener. Aborting both removes the
   * listener and clears that state in one step, which matters because
   * the two must never disagree: a slot left marked armed after its
   * video is gone can never arm again, and would silently lose the
   * upload-on-seek fix for the rest of the session.
   */
  private siblingSeekUploads: Array<AbortController | null> = []
  /** Pending re-check after a repair attempt — see `verifySiblingTimes`. */
  private siblingRepairTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * The multi-monitor output link (`docs/MULTI_MONITOR_PLAN.md` §3).
   *
   * Desktop-only and inert on web — the gate is inside
   * `startMultiOutput`, which returns a shared no-op handle there.
   * Held as a field only so `dispose()` can detach it.
   */
  private multiOutput: MultiOutputBootHandle | null = null

  /**
   * Fullscreen + decorations for this window (§3.6). Held so the Tools
   * menu can read the state and `dispose()` can detach the F11 handler.
   */
  private fullscreen: FullscreenController | null = null
  private idleCursor: IdleCursor | null = null

  /**
   * Convenience getter returning the primary viewport's renderer.
   * Most call sites operate on a single "the renderer" for the
   * primary panel; per-slot operations go through `this.viewports`
   * and `this.panelStates` directly.
   */
  private get renderer(): MapRenderer | null {
    return this.viewports.getPrimary()
  }

  /** Primary panel's HLS service, if any. */
  private get hlsService(): HLSService | null {
    return this.panelStates[this.viewports.getPrimaryIndex()]?.hlsService ?? null
  }

  /** Primary panel's video texture, if any. */
  private get videoTexture(): VideoTextureHandle | null {
    return this.panelStates[this.viewports.getPrimaryIndex()]?.videoTexture ?? null
  }

  private playback: PlaybackState = createPlaybackState()
  private loadingHideTimer: ReturnType<typeof setTimeout> | null = null
  private loadGeneration = 0 // guards against concurrent dataset loads
  private tourEngine: TourEngine | null = null
  private tourIsStandalone = false // true when tour was loaded as a tour/json dataset (not runTourOnLoad)

  /** Persisted view preferences: info panel + legend visibility. */
  private viewPrefs: ViewPreferences = loadViewPreferences()

  /**
   * The data-encoded viewing transform, shared by every panel.
   *
   * Session-scoped rather than persisted: a palette or threshold is
   * chosen against the field in front of you, and silently re-applying
   * last week's threshold to a different dataset would hide data with
   * no visible cause. It does survive dataset and layout changes within
   * a session, which is the span over which a viewer is comparing.
   */
  private colorScaleDisplay: ColorScaleDisplay = DEFAULT_DISPLAY

  /**
   * Which slot's dataset the info panel currently displays.
   *
   * - `null` → follow the primary. The info panel re-renders whenever
   *   the primary changes.
   * - A slot index → "pinned" to that slot. The user explicitly picked
   *   a non-primary dataset from the picker dropdown. Resets to null
   *   whenever the primary changes (via `onViewportPrimaryChange`) so
   *   a fresh promotion doesn't silently keep pointing at an old slot.
   */
  private infoDisplayOverride: number | null = null

  /** True once the info-selector change handler has been wired. */
  private infoSelectorWired = false

  /**
   * Boot the application: create the WebGL renderer, fetch the dataset
   * catalog, and either load a URL-specified dataset or show the default
   * Earth with the browse panel.
   */
  async initialize(nodeFeatures: PendingNodeFeatures = startNodeFeaturesRead()): Promise<void> {
    // This node's feature toggles, of which boot needs one: whether
    // Orbit is offered at all. Started before the i18n bootstrap (see
    // the DOMContentLoaded handler) so it overlaps everything up to
    // here, and read outside the `try` because both outcomes of it need
    // the answer: the chat is set up inside, and the deep links after
    // the `catch` can open the panel even when boot failed. `settle`
    // never rejects and bounds its own wait (all-enabled past it).

    try {
      this.setLoading(true)
      this.setLoadingStatus('Starting up\u2026', 5)

      // Catalog mode (`?catalog=true`) inverts the default surface:
      // the dataset browser becomes primary and the globe stays
      // hidden until selection. Apply the body class before any UI
      // renders so the CSS surface decision (full-width browse vs.
      // overlay browse) is made on the first paint, not after a
      // flash of the globe-first layout. See
      // `docs/WEB_CATALOG_FEATURES_PLAN.md` \u00a73.
      const catalogModeActive = getCatalogMode()
      if (catalogModeActive) document.body.classList.add('catalog-mode')

      // Embed mode (`?embed=1`) strips app chrome for iframe hosting
      // (WordPress blocks, kiosk, the poster). Apply before any UI
      // renders — same first-paint reasoning as catalog mode — so the
      // chrome never flashes in. Composes with `?dataset=`/`?tour=`/
      // `?catalog=true`. See `docs/EMBED_URL_GRAMMAR.md`.
      const embedModeActive = applyEmbedMode()

      if (!this.checkWebGLSupport()) return

      const container = document.getElementById('container')
      if (!container) throw new Error('Container element not found')
      const mapGrid = document.getElementById('map-grid')
      if (!mapGrid) throw new Error('Map grid element not found')

      this.setLoadingStatus('Creating renderer\u2026', 15)
      const initialLayout = this.getInitialLayoutFromUrl()
      this.viewports.init(mapGrid, initialLayout, {
        onLayoutChange: (newCount, oldCount) => this.onViewportLayoutChange(newCount, oldCount),
        onPrimaryChange: (newIdx, oldIdx) => this.onViewportPrimaryChange(newIdx, oldIdx),
        getLayerIdForSlot: (slot) => this.panelStates[slot]?.dataset?.id ?? null,
      })
      // Parallel per-panel dataset state, one entry per viewport.
      this.panelStates = Array.from({ length: this.viewports.getPanelCount() }, createPanelState)
      const primary = this.viewports.getPrimary()
      if (!primary) throw new Error('Viewport manager failed to create a primary renderer')
      // Mirror globe state to any multi-monitor outputs. Returns
      // synchronously, so the subscription is installed before the
      // first dataset load below can publish; opening the IPC link and
      // spawning windows wait for the operator to add an output, so
      // until then this costs a listener and nothing else.
      //
      // Ahead of `initToolsMenu` because the menu decides whether to
      // render its Outputs entry while it builds its markup, and that
      // decision reads `available` off this handle.
      this.multiOutput = startMultiOutput({
        // The control window's own contribution to the machine's
        // decoder budget. Every panel is a window that can hold a
        // video, and `maxVideoPanels()` — which the manager seeds from
        // — answers per window, so nothing but this can tell it that
        // four globes and an output are five decoders on one GPU.
        controlPanels: () => this.viewports.getPanelCount(),
      })
      initOutputUI({ manager: () => this.multiOutput?.ready ?? Promise.resolve(null) })
      // Outputs follow the primary globe's camera (§3, rung 7). After
      // `startMultiOutput` so the subscription that forwards this is
      // already installed, and before the first dataset load below.
      this.bindOperatorCamera()
      // Window chrome (§3.6). Built before the Tools menu because the
      // menu reads the fullscreen state while it builds its markup —
      // the same reason `startMultiOutput` runs first, and why the
      // desktop host defers its Tauri import rather than being awaited.
      this.initWindowChrome()
      initToolsMenu(this.viewports, {
        onSetLayout: (layout) => this.viewports.setLayout(layout),
        onOpenBrowse: () => this.openBrowsePanel(),
        onOpenOrbitSettings: () => openChatSettings(),
        onOpenCredits: (trigger) => openCreditsPanel(this.viewports, trigger),
        // Desktop-only: on web `startMultiOutput` hands back the shared
        // inert handle, this is `undefined`, and the menu renders no
        // Outputs section at all.
        onOpenOutputs: this.multiOutput.available
          ? (trigger) => { openOutputUI(trigger) }
          : undefined,
        onToggleDatasetInfo: (visible) => this.setDatasetInfoVisible(visible),
        onToggleLegend: (visible) => this.setLegendVisible(visible),
        announce: (msg) => this.announce(msg),
        getCurrentDataset: () => this.appState.currentDataset ?? null,
        fullscreen: this.fullscreen ?? undefined,
      })
      // Catalog ↔ sphere tab control — only becomes visible when
      // `?catalog=true` is in the URL (see the show/hide calls in
      // the boot branches further below and in
      // `wireCatalogModePopstate` for history navigation). The
      // control is mounted unconditionally so the popstate handler
      // can flip its visibility without re-wiring listeners.
      initCatalogTabs({
        onSelectCatalog: () => this.enterCatalogTab(),
        onSelectSphere: () => this.enterSphereTab(),
      })
      this.wireCatalogModePopstate()
      // Apply persisted view prefs to the toolbar button state now
      // that the toolbar exists.
      syncToolsMenuState({
        datasetInfo: this.viewPrefs.infoPanelVisible,
        legend: this.viewPrefs.legendVisible,
      })
      initDownloadUI().catch(err => logger.warn('[App] Download UI init failed:', err))
      // Web-only zip-download dialog (§8.2). Mounting is cheap and
      // safe on desktop too; the opener affordances are the gate.
      initDownloadDialogUI({ announce: (msg) => this.announce(msg) })
      initHelpUI()
      // Statistics over the frame on screen (§A3). Reads through the
      // primary panel, which is the one the info panel, playback and
      // the value readout all already follow — analysing a globe the
      // user is not driving would be a different feature.
      initAnalyzeUI({
        frame: () => this.viewports.getPrimary()?.analysisFrame() ?? null,
        visibleBounds: () => this.viewports.getPrimary()?.visibleBounds() ?? null,
        display: () => this.colorScaleDisplay,
        datasetTitle: () => this.appState.currentDataset?.title ?? null,
        datasetId: () => this.appState.currentDataset?.id ?? null,
        // Primary panel only, matching the probe readout: `probeValueAt`
        // is a MapRenderer method rather than part of the GlobeRenderer
        // interface, so a transect on a secondary panel would have
        // nothing to sample. Named as a constraint in
        // `docs/DATA_ANALYSIS_PLAN.md` rather than worked around here.
        transect: () => {
          const primary = this.viewports.getPrimary()
          if (!primary) return null
          return {
            begin: (onChange) => primary.beginTransect(onChange),
            progress: () => primary.transectProgress(),
            clear: () => primary.clearTransect(),
          }
        },
        regionOutline: () => {
          const primary = this.viewports.getPrimary()
          if (!primary) return null
          return {
            show: (bounds) => primary.showRegionOutline(bounds),
            clear: () => primary.clearRegionOutline(),
          }
        },
        contours: () => {
          const primary = this.viewports.getPrimary()
          if (!primary) return null
          return {
            show: (levels) => primary.showContours(levels),
            clear: () => primary.clearContours(),
          }
        },
        // The same label Orbit's tool results are stamped with, so the
        // panel and an Orbit answer can never name different frames for
        // the same measurement.
        frameTime: () => readCurrentTime(),
        // Identity, not display. `readCurrentTime` reads the *label*,
        // which is snapped to the display interval and hidden outright
        // for a dataset without start/end times — useless for telling
        // whether the frame moved. This is the video playhead, the same
        // value the luma sampler keys its snapshot cache on.
        frameId: () => this.viewports.getPrimary()?.currentFrameId() ?? null,
      })
      // The same frame, reachable from Orbit's tool executors (§A6).
      // Registered rather than passed down: `processMessage` already
      // takes eight positional arguments, and the panel's own seam is
      // established as a registration too. Reads the primary panel for
      // the same reason the panel does.
      registerAnalysisSource({
        frame: () => this.viewports.getPrimary()?.analysisFrame() ?? null,
        visibleBounds: () => this.viewports.getPrimary()?.visibleBounds() ?? null,
        datasetTitle: () => this.appState.currentDataset?.title ?? null,
        // Deliberately the renderer's own probe rather than a second
        // read of `analysisFrame()`. The point is to ask a different
        // path the same question: this is the call the hover readout
        // makes, with the renderer's probe source and bounding box, so
        // if it and the snapshot have drifted apart the cross-check
        // sees it. Reading the frame again here would agree with
        // itself and prove nothing.
        probeAt: (lat, lon) => {
          const reading = this.viewports.getPrimary()?.probeValueAt(lat, lon)
          if (!reading) return null
          return { value: reading.value, noData: reading.noData === true }
        },
      })
      // Playlists — mount the manager panel host and wire the
      // playback state machine to the regular loadDataset flow.
      // hasTourOnLoad probes dataset metadata so the playback
      // module can defer its auto-advance timer while a tour is
      // running. notifyPlaylistTourEnded fires from endTour() below.
      initPlaylistUI({ announce: (msg) => this.announce(msg) })
      initPlaylistPlayback({
        loadDataset: async (id) => {
          await this.loadDataset(id, 'url')
        },
        hasTourOnLoad: (id) => {
          const dataset = dataService.getDatasetById(id)
          return !!dataset?.runTourOnLoad
        },
      })
      // Toggle the transport "Skip to next playlist item" button as
      // a playlist becomes active / inactive. Lives at boot so the
      // listener survives every dataset swap.
      const syncPlaylistNextBtn = () => {
        const btn = document.getElementById('playlist-next-btn')
        if (!btn) return
        btn.classList.toggle('hidden', !getActivePlaylistPlayback())
      }
      onPlaylistPlaybackChange(syncPlaylistNextBtn)
      syncPlaylistNextBtn()
      // First-session privacy disclosure. No-ops on every launch
      // after the user dismisses it. Skipped inside an embed — the
      // host page owns consent, and a privacy banner popping up in a
      // third-party iframe is both wrong and intrusive (§J of
      // `docs/WORDPRESS_INTEGRATION_PLAN.md`).
      if (!embedModeActive) showDisclosureBannerIfNeeded()
      // Telemetry transport — skipped entirely when the compile-time
      // flag is off (telemetry-free builds) and when console mode
      // is on (dev convenience: events log locally, no POSTs).
      if (TELEMETRY_BUILD_ENABLED && !TELEMETRY_CONSOLE_MODE) {
        setTransport(createFetchTransport())
      }
      logger.info('[App] Using MapLibre renderer (layout: %s)', initialLayout)

      // Wire up lat/lng display (bind to primary; secondary panels don't
      // update the display — it follows the primary's mousemove)
      const latlngEl = document.getElementById('latlng-display')
      if (latlngEl) {
        primary.setLatLngCallbacks(
          (lat: number, lng: number) => {
            const ns = lat >= 0 ? 'N' : 'S'
            const ew = lng >= 0 ? 'E' : 'W'
            const coords = `${Math.abs(lat).toFixed(1)}° ${ns}, ${Math.abs(lng).toFixed(1)}° ${ew}`
            // A data-encoded dataset can also say what the value
            // *is* here. Everything else reports coordinates only,
            // exactly as before.
            const reading = primary.probeValueAt(lat, lng)
            latlngEl.textContent = reading
              ? `${coords} · ${formatProbeReading(reading)}`
              : coords
            latlngEl.classList.remove('hidden')
          },
          () => {
            latlngEl.classList.add('hidden')
          }
        )
      }

      // Fetch datasets, then load from URL if specified
      this.setLoadingStatus('Loading datasets\u2026', 30)
      await this.loadDatasets()

      // Initialize digital docent chat (available on all views) —
      // unless this node turned Orbit off, decided here, before
      // anything below can show the trigger or open the panel.
      this.initChat((await nodeFeatures.settle()).orbit)

      // Tour-authoring dock — mounts only when the URL carries
      // `?tourEdit=<id>`. Phase 3pt/A. Idempotent; the dock owns
      // its own DOM and dispose lifecycle.
      this.initTourAuthoring()

      // Enter VR button — feature-gated internally; hides itself on
      // non-WebXR browsers and warm-loads Three.js in the background
      // on devices that can enter VR.
      void this.wireVrButton()

      // Preview deeplink: `?preview=<token>&dataset=<id>` lands a
      // draft dataset on the globe by fetching the token-gated
      // metadata + manifest endpoints (3pe/A + 3pe/B). The boot
      // flow runs *after* loadDatasets so the regular catalog is
      // still populated \u2014 the preview row is injected alongside,
      // not in place of, the public catalog. On error we surface
      // a typed message via setError and fall through to the
      // empty-globe path so the visitor isn't left staring at a
      // blank loading overlay with no globe and no browse panel.
      // Falling through bypasses the `?dataset=` branch below
      // because that id won't be in the public catalog (it's the
      // draft we just failed to fetch) \u2014 re-attempting it would
      // overwrite our typed preview error with a generic
      // "Dataset not found".
      const previewParams = this.getPreviewParamsFromUrl()
      let previewFailed = false
      if (previewParams) {
        this.setLoadingStatus('Loading preview\u2026', 50)
        try {
          const preview = await dataService.fetchPreviewDataset(
            previewParams.datasetId,
            previewParams.token,
          )
          // Inject into the dataService cache so the existing
          // loadDataset path (which resolves by id via
          // getDatasetById) finds the draft. appState.datasets is
          // left as the public catalog snapshot \u2014 the browse panel
          // continues to show what an unauthenticated visitor
          // would see; only the active globe shows the draft.
          dataService.injectDataset(preview)
          await this.loadDataset(preview.id, 'url')
          this.setLoading(false)
          this.runUrlLoadUiSync()
          return
        } catch (err) {
          const message = err instanceof PreviewFetchError
            ? previewErrorMessage(err.code)
            : err instanceof Error
              ? err.message
              : 'Failed to load preview.'
          this.setError(message)
          previewFailed = true
        }
      }

      const datasetId = previewFailed ? null : this.getDatasetIdFromUrl()
      if (datasetId) {
        // Canonicalize whatever form the visitor arrived on \u2014 an old
        // `?dataset=<ULID>` link, or `/dataset/<legacy_id>` \u2014 to
        // `/dataset/<slug>`, so the address bar reads as something
        // worth pasting into a message and anything copied from here
        // spreads the human-friendly form.
        this.writeDatasetUrl(datasetId, 'replace')
        this.setLoadingStatus('Loading dataset\u2026', 50)
        await this.loadDataset(datasetId, 'url')
        this.setLoading(false)
        this.runUrlLoadUiSync()
        // In multi-viewport mode, pre-render the browse panel in its
        // collapsed state so users can slide it open to load datasets
        // into the remaining panels, and pulse the Browse button so
        // they notice where to click. In single-view mode we don't —
        // the URL-specified dataset is the only thing the user wanted.
        //
        // Catalog mode (`?catalog=true&dataset=<id>`) gets the same
        // treatment as multi-viewport: render the browse panel
        // collapsed so the tab control's "back to catalog" affordance
        // has something to expand. Plan §3.1 case 2.
        if (this.viewports.getPanelCount() > 1 || catalogModeActive) {
          showBrowseUI(this.appState.datasets, {
            onSelectDataset: (id) => this.selectDatasetFromBrowse(id),
            announce: (msg) => this.announce(msg),
            isMobile: this.isMobile,
            onOpenChat: this.browseOpenChat(),
          })
          collapseBrowseUI()
          if (!catalogModeActive) pulseBrowseButton()
        }
        if (catalogModeActive) {
          showCatalogTabs()
          setActiveCatalogTab('sphere')
        }
      } else if (catalogModeActive) {
        // Catalog-mode no-dataset boot: the browse panel is the
        // primary surface and the globe stays hidden until the
        // visitor picks a dataset. Skip the Earth-textures load
        // since nothing is going to show them; the regular
        // dataset-load path runs them on demand when the visitor
        // exits catalog mode by selecting something.
        //
        // `catalog-empty` is the CSS hook for "globe hidden,
        // browse full-surface"; it stays set until the first
        // dataset loads (cleared in `selectDatasetFromBrowse`).
        // `catalog-mode` is the sticky session marker \u2014 set once
        // when the visitor entered via `?catalog=true` and never
        // removed within the session so the catalog\u2194sphere tab
        // control stays visible even after they flip to Sphere.
        document.body.classList.add('catalog-empty')
        this.setLoading(false)
        showBrowseUI(this.appState.datasets, {
          onSelectDataset: (id) => this.selectDatasetFromBrowse(id),
          announce: (msg) => this.announce(msg),
          isMobile: this.isMobile,
          onOpenChat: this.browseOpenChat(),
        })
        // No collapse, no hide, no pulse \u2014 the browse panel is
        // the visible surface, not an overlay competing with the
        // globe for attention.
        showCatalogTabs()
        setActiveCatalogTab('catalog')
      } else {
        this.setLoadingStatus('Loading Earth textures\u2026', 20)
        const cloudUrl = CLOUD_TEXTURE_URL

        let earthFraction = 0
        let cloudFraction = 0
        const updateProgress = () => {
          const combined = earthFraction * EARTH_TEXTURE_WEIGHT + cloudFraction * CLOUD_TEXTURE_WEIGHT
          this.setLoadingStatus('Loading Earth textures\u2026', LOADING_BASE_PROGRESS + Math.round(combined * LOADING_TEXTURE_RANGE))
        }
        // Earth materials are a visual enhancement (day/night, clouds,
        // specular) — if loading them times out or fails (common in
        // multi-viewport mode on mobile when 4 WebGL contexts compete
        // for bandwidth), the base tiles still render fine. Don't let
        // the failure abort the init flow and block the browse panel.
        try {
          await Promise.all([
            primary.loadDefaultEarthMaterials((f: number) => { earthFraction = f; updateProgress() }),
            primary.loadCloudOverlay(cloudUrl, (f: number) => { cloudFraction = f; updateProgress() })
          ])
          // Only if the globe is still bare. `enableSunLighting` clears
          // the dataset texture and the probe source by design — that
          // is how unloading returns to the plain Earth — so firing it
          // here after a dataset has already been loaded wipes that
          // dataset off the globe and silently kills its value readout.
          //
          // The window is real whenever these two textures are slow or
          // unreachable: they are large external assets, and the
          // catalog can be ready long before them. The dataset is the
          // thing the visitor asked for; the day/night look is not.
          if (!this.panelStates.some(p => p?.dataset)) {
            const sun = getSunPosition(new Date())
            primary.enableSunLighting(sun.lat, sun.lng)
          }
        } catch (err) {
          logger.warn('[App] Earth material loading failed — continuing without day/night overlay:', err)
        }

        this.setLoading(false)
        // Render the browse panel (populates category filters and
        // dataset cards) then decide whether to leave it visible.
        // - Multi-viewport mode: the visitor explicitly chose
        //   multiple globes (via ?layout= or the layout picker).
        //   Don't cover one of them with the browse panel by
        //   default — collapse it and pulse the Browse button so
        //   they notice where to click. Mirrors the same decision
        //   in the dataset-from-URL branch above.
        // - Mobile single-view: panel is full-width and would
        //   hide the globe entirely; start hidden and pulse.
        // - Desktop single-view: keep the existing side-panel UX
        //   so the visitor sees the catalog by default.
        showBrowseUI(this.appState.datasets, {
          onSelectDataset: (id) => this.selectDatasetFromBrowse(id),
          announce: (msg) => this.announce(msg),
          isMobile: this.isMobile,
          onOpenChat: this.browseOpenChat(),
        })
        if (this.viewports.getPanelCount() > 1) {
          collapseBrowseUI()
        } else if (window.matchMedia('(max-width: 768px)').matches) {
          hideBrowseUI()
        }
        pulseBrowseButton()
      }
    } catch (error) {
      this.setLoading(false)
      this.setError(error instanceof Error ? error.message : 'Unknown error')
    }

    // A boot that failed before `initChat` still owes the Orbit
    // decision: the deep links below can open the panel or show the
    // trigger. Idempotent when `initChat` already applied it.
    if (!(await nodeFeatures.settle()).orbit) setOrbitAvailable(false)

    // Phase 5: listen for deep links unconditionally so the app can
    // load a dataset when opened from an external URL at any time,
    // not just when a ?dataset= query param is present at startup.
    // The Tauri native deep-link plugin fires `zyra://dataset/<id>`
    // and `https://terraviz.zyra-project.org/dataset/<id>` events
    // mid-session; route them through the same post-load UI sync
    // that the boot ?dataset= and poster ?tour= paths use, so the
    // chat trigger / canvas description / help overlay all stay in
    // sync regardless of which entry point the load came from.
    initDeepLinks(async (id) => {
      await this.loadDataset(id, 'url')
      this.runUrlLoadUiSync()
    })

    // Apply any poster-flavoured deep-link query params (?tour=,
    // ?terrain=, ?labels=, ?borders=, ?rotate=, ?orbit=). These run
    // after the catalog is loaded and the Tools menu is wired so the
    // dispatch can route through canonical APIs and button click
    // handlers without any race risk. ?layout= and ?dataset= are
    // already handled by the existing initial-load path above. The
    // call is awaited so a ?tour= load completes before subsequent
    // ?orbit=open / view-toggle dispatches run on top of it.
    //
    // The loadDataset callback runs the same post-load UI sync the
    // boot ?dataset= path does (showChatTrigger, notifyDatasetChanged,
    // setHelpActiveDataset). Without it, a poster URL like
    // ?tour=climate-futures&orbit=open opens the chat panel but the
    // floating Orbit trigger stays hidden — so if the visitor closes
    // the chat panel they have no way to reopen it.
    await applyPosterDeepLinks({
      catalog: this.appState.datasets,
      loadDataset: async (id) => {
        await this.loadDataset(id, 'url')
        this.runUrlLoadUiSync()
      },
      openChatWithQuery: (query) => this.openChatWithQuery(query),
    })
  }

  /**
   * Post-load UI sync that every URL-driven dataset load needs to
   * run on top of `loadDataset()`: surface the floating Orbit chat
   * trigger so the visitor can re-open chat after closing it,
   * refresh the canvas's accessible description so screen readers
   * stop announcing the stale Earth title, announce the dataset
   * change to the chat panel so its system prompt picks up the
   * new context, and tell the help overlay which dataset is now
   * active.
   *
   * Browse-panel and chat-driven loads (`selectDatasetFromBrowse`,
   * `selectDatasetFromChat`) call equivalent code inline; URL
   * paths share this helper — boot `?dataset=`, the
   * `applyPosterDeepLinks` poster pipeline, and the Tauri
   * `zyra://`/`/dataset/...` native deep-link listener — so the
   * sequencing stays consistent across every URL-driven entry
   * point.
   */
  private runUrlLoadUiSync(): void {
    showChatTrigger()
    const dataset = this.appState.currentDataset
    if (dataset) {
      this.renderer?.setCanvasDescription(`3D globe showing ${dataset.title}`)
      notifyDatasetChanged(dataset)
      setHelpActiveDataset(dataset.id)
    }
    // Catalog-mode integration — a URL-driven load just resolved
    // (dataset, preview, tour, or native deep-link), which means
    // the globe is now the active surface. Drop the
    // `catalog-empty` flag so the CSS rule that hides `#map-grid`
    // releases, and (if we're still in catalog mode) flip the
    // tab control to Sphere active. Mirrors the inline sync that
    // `selectDatasetFromBrowse` does for browse-driven loads.
    //
    // Plan §3.5 explicitly required this for the tour-in-catalog
    // path ("tours assume globe is mounted; if a tour starts in
    // catalog mode, it must implicitly flip to sphere mode") and
    // the `?catalog=true&preview=…` consumer would otherwise
    // load the draft dataset to an invisible canvas. Centralising
    // the sync here covers every URL-driven entry point through
    // a single hook.
    document.body.classList.remove('catalog-empty')
    if (getCatalogMode()) {
      showCatalogTabs()
      setActiveCatalogTab('sphere')
    }
  }

  /** Extract the dataset to boot with from the current URL — the
   *  canonical `/dataset/<slug>` path form, or the `?dataset=` query
   *  param older links carry. Either may name the dataset by slug,
   *  ULID, or legacy id; all three resolve through
   *  `getDatasetById`. A well-formed reference that isn't in the
   *  catalog is returned as-is, so the loader surfaces a "not found"
   *  naming what the visitor actually asked for.
   *
   *  Both forms are validated against the same alphabet. The path
   *  form gets that from `parseDatasetPathname`; applying it to the
   *  query form too means a malformed `?dataset=` value (markup,
   *  spaces) is ignored rather than laundered into a
   *  `/dataset/<percent-encoded-junk>` path by the canonicalize step
   *  — a URL that wouldn't parse back on reload. */
  private getDatasetIdFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('dataset') ?? parseDatasetPathname(window.location.pathname)
    if (!raw || !isDatasetRef(raw)) return null
    return dataService.getDatasetById(raw)?.id ?? raw
  }

  /**
   * Write the canonical `/dataset/<slug>` URL for a dataset into the
   * address bar. A reference that isn't in the loaded catalog is
   * written through as-is (`/dataset/<ref>`) rather than resolved —
   * the resulting URL still boots to the same "not found", and it
   * keeps naming what the visitor actually asked for.
   *
   * Leaves a token-gated draft preview's URL alone *while the draft
   * is what's being written* — `?preview=` is only valid with its
   * token attached, so canonicalizing it to the public path would
   * 404 on reload. Switching to a different dataset from inside a
   * preview session is a real navigation and gets a real URL:
   * `buildDatasetPath` drops the spent token. Skipping that write
   * would leave the address bar naming the draft while the globe
   * showed something else, and a reload would snap back to the
   * draft.
   */
  private writeDatasetUrl(id: string, mode: 'push' | 'replace'): void {
    const search = window.location.search
    const previewRef = previewDatasetRef(search)
    if (previewRef !== null) {
      const previewId = dataService.getDatasetById(previewRef)?.id ?? previewRef
      if (previewId === id) return
    }
    const next = buildDatasetPath(dataService.getDatasetById(id) ?? { id }, search)
    if (next === `${window.location.pathname}${search}`) return
    if (mode === 'push') {
      window.history.pushState({}, '', next)
    } else {
      window.history.replaceState({}, '', next)
    }
  }

  /** Reset the address bar to the no-dataset form, keeping whatever
   *  mode the visitor arrived in (`?catalog=true`, `?embed=1`). */
  private clearDatasetUrl(mode: 'push' | 'replace'): void {
    const next = buildNoDatasetPath(window.location.pathname, window.location.search)
    if (mode === 'push') {
      window.history.pushState({}, '', next)
    } else {
      window.history.replaceState({}, '', next)
    }
  }

  /**
   * Extract `?preview=<token>&dataset=<id>` from the current URL.
   * Returns null when either is missing — both are required for the
   * preview consumer to know what to fetch and which token to
   * present. Short-circuits early so the regular `?dataset=` path
   * (without a token) keeps working unchanged.
   */
  private getPreviewParamsFromUrl(): { token: string; datasetId: string } | null {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('preview')
    const datasetId = params.get('dataset')
    if (!token || !datasetId) return null
    return { token, datasetId }
  }

  /**
   * Read the initial viewport layout from the URL. Prefers
   * `?layout=` (the public form used by the companion poster's
   * deep-link buttons); falls back to the legacy dev `?setview=`
   * for backwards compatibility. Both forms accept the same set
   * of values: `1`, `2`, `2h`, `2v`, `4`. The shorthand `2`
   * resolves to `2h`. Unknown / missing values default to
   * single-view (`1`).
   */
  private getInitialLayoutFromUrl(): ViewLayout {
    return parseInitialLayout(window.location.search)
  }

  /** Fetch the dataset catalog from the data service and store in app state. */
  private async loadDatasets(): Promise<void> {
    const datasets = await dataService.fetchDatasets()
    this.appState.datasets = datasets
    // §9.2 — once the catalog is in hand, light the "new since your
    // last visit" badge on the Browse trigger if anything was added
    // after the previous session ended. No-op on a first-ever visit
    // (no stored lastSession) — fail-closed in countNewSince.
    refreshBrowseNewSinceBadge(datasets)
  }

  /** Load a dataset by ID onto the globe, tearing down any previous video stream first. Uses a generation counter to safely ignore superseded loads. */
  private async loadDataset(
    datasetId: string,
    trigger: import('./types').LoadTrigger = 'default',
  ): Promise<void> {
    const gen = this.loadGeneration
    const loadStartWall = Date.now()
    logger.debug('[App] loadDataset start:', datasetId)
    stopPlaybackLoop(this.playback)
    this.appState.isPlaying = false
    resetPlaybackState(this.playback)

    // Emit layer_unloaded for whatever was in the primary panel before
    // the new dataset supersedes it. "replaced" covers both user-driven
    // browses and orbit-driven loads; `goHome` has its own reason.
    this.emitLayerUnloadedForSlot(this.viewports.getPrimaryIndex(), 'replaced')

    // Stop any active tour (but don't trigger full goHome — let the new load proceed)
    this.stopTour()

    // Tear down the previous video *before* starting the new load so the old
    // HLS stream stops downloading and the old MediaSource is released. Two
    // concurrent HLS.js instances fight over bandwidth and can exhaust the
    // browser's MediaSource / SourceBuffer limits, stalling the new load.
    this.cleanupPanelVideo()

    this.renderer?.removeCloudOverlay()
    this.renderer?.removeNightLights()
    this.renderer?.disableSunLighting()

    try {
      await this.displayDataset(datasetId, gen, trigger, loadStartWall)
      if (gen !== this.loadGeneration) {
        logger.debug('[App] loadDataset superseded:', datasetId)
        this.cleanupVideo()
        return
      }
      this.showHomeButton()
      logger.debug('[App] loadDataset complete:', datasetId)
    } catch (error) {
      // displayDataset clears its own loading overlay via a
      // try/finally scoped to the slot it pinned at entry, so we
      // don't need to touch it here — doing so risks clearing the
      // wrong panel if the user promoted a different slot mid-load.
      if (gen !== this.loadGeneration) {
        logger.debug('[App] loadDataset superseded (error ignored):', datasetId)
        this.cleanupVideo()
        return
      }
      logger.debug('[App] loadDataset failed:', datasetId, error)
      // Clean up any partially-created resources from the failed load
      this.cleanupVideo()
      this.setError(error instanceof Error ? error.message : 'Failed to load dataset')
    }
  }

  /** Resolve, render, and apply a dataset (image or video) to the sphere. */
  private async displayDataset(
    datasetId: string,
    gen: number,
    trigger: import('./types').LoadTrigger = 'default',
    loadStartWall: number = Date.now(),
  ): Promise<void> {
    const dataset = dataService.getDatasetById(datasetId)
    if (!dataset) throw new Error(`Dataset not found: ${datasetId}`)

    // §9.2 — count a user-initiated open as a visit for the
    // Continue-exploring row. Tours never reach this path (they go
    // through loadDatasetForTour), so they're excluded for free.
    // Playlist auto-advance shares the 'url' trigger with deep links,
    // so exclude it explicitly: an auto-advanced dataset isn't a
    // deliberate "I opened this" signal. viewSeconds still accrues
    // separately from info-panel reading time (datasetLoader).
    const isPlaylistAutoAdvance = trigger === 'url' && getActivePlaylistPlayback() != null
    if (!isPlaylistAutoAdvance) {
      recordVisit(dataset.id)
    }

    // Pin the target slot + its renderer once, at the very start. Both
    // must stay fixed for the lifetime of this call — the awaits below
    // span image decode / HLS manifest fetches, during which the user
    // can promote a different panel to primary. Reading `this.renderer`
    // (a live getter over the primary) after an await would then apply
    // the new dataset's texture to a different panel than the one we
    // recorded in `panelStates`, leaving the two in disagreement and
    // letting a single load bleed across globes.
    const targetSlot = this.viewports.getPrimaryIndex()
    const targetRenderer = this.viewports.getRendererAt(targetSlot)
    if (!targetRenderer) throw new Error('Renderer not initialized')

    this.appState.currentDataset = dataset
    if (this.panelStates[targetSlot]) {
      this.panelStates[targetSlot].dataset = dataset
    }
    // Register the dataset's attribution as a phantom MapLibre
    // source so Tools → Credits picks it up. See creditsPanel.ts
    // for the design. No-op on non-MapRenderer surfaces.
    if (targetRenderer instanceof MapRenderer) {
      targetRenderer.setDatasetCredits(dataset)
    }

    logger.info('[App] Loading dataset:', {
      id: dataset.id,
      title: dataset.title,
      format: dataset.format,
      slot: targetSlot,
      hasTimeData: !!(dataset.startTime && dataset.endTime)
    })

    // Loading a new dataset into the primary resets any picker
    // override — the user probably wants to see their fresh load.
    this.infoDisplayOverride = null
    this.renderInfoPanel()
    this.refreshPanelLegends()

    const loaderCallbacks = {
      showPlaybackControls: (show: boolean) => this.showPlaybackControls(show),
      showTimeLabel: (show: boolean) => this.showTimeLabel(show),
    }

    // Show a per-panel loading overlay so the user sees "Loading…"
    // instead of the confusing Blue Marble intermediate state. Wrap
    // the whole async body in try/finally so the overlay always
    // clears on the slot we pinned — even if the load throws and
    // even if the user promotes a different panel mid-flight.
    this.viewports.setPanelLoading(targetSlot, true, `Loading ${dataset.title}\u2026`)

    try {
      if (dataset.format === 'tour/json') {
        this.tourIsStandalone = true
        // The node-catalog source surfaces the actual tour-fetch
        // URL on `tourJsonUrl`; the manifest endpoint at
        // `dataLink` 415s tour formats. Legacy SOS catalog
        // responses don't carry `tourJsonUrl` — fall back to
        // `dataLink` (which on those rows is the direct
        // CloudFront URL).
        const tourUrl = dataset.tourJsonUrl ?? dataset.dataLink
        await this.startTour(tourUrl, gen, null, {
          tourId: dataset.id,
          tourTitle: dataset.title,
          source: triggerToTourSource(trigger),
        })
        return
      } else if (dataService.isImageDataset(dataset)) {
        const img = await loadImageDataset(dataset, targetRenderer, this.appState, this.isMobile, loaderCallbacks)
        if (gen !== this.loadGeneration) return
        if (this.panelStates[targetSlot]) {
          this.panelStates[targetSlot].image = img
          this.panelStates[targetSlot].mediaDatasetId = dataset.id
        }
        this.emitLayerLoaded(dataset, targetSlot, trigger, 'image', Date.now() - loadStartWall)
      } else if (dataService.isVideoDataset(dataset)) {
        // Clear any previously-cached image element for this slot —
        // if the user just switched from an image dataset to a video
        // dataset, the old decoded image is no longer referenced and
        // should be released so its backing bytes can be GC'd.
        if (this.panelStates[targetSlot]) this.panelStates[targetSlot].image = null
        const result = await loadVideoDataset(
          dataset, targetRenderer, this.appState, this.isMobile, this.playback, loaderCallbacks
        )
        // If a newer load started while we were awaiting, discard these results.
        // Don't dispose videoTexture here — setVideoTexture already placed it on
        // the sphere material, so the next load's setVideoTexture will replace it.
        if (gen !== this.loadGeneration) {
          result.hlsService.destroy()
          return
        }
        this.storePanelVideoResult(targetSlot, result, dataset.id)
        this.attachPrimaryVideoSync()
        this.doStartPlaybackLoop()
        this.emitLayerLoaded(dataset, targetSlot, trigger, 'hls', Date.now() - loadStartWall)
      } else {
        throw new Error(`Unsupported format: ${dataset.format}`)
      }
    } finally {
      // Guard against a superseded load hiding the newer load's
      // overlay: only clear if we're still the most recent load.
      if (gen === this.loadGeneration) {
        this.viewports.setPanelLoading(targetSlot, false)
      }
    }

    // Fetch and cache the legend image; generate a text description for non-vision mode.
    this.primeOrbitLegend(dataset)

    // Auto-start a tour if the dataset has one associated via runTourOnLoad.
    // Skip if a tour is already running (the tour engine triggered this load).
    // Failures are silently logged — the tour is optional, the dataset already loaded.
    //
    // Pin the tour to the slot we just loaded into. Legacy SOS tour
    // JSON uses `worldIndex: 1` to mean "the current globe", which
    // the tour engine would otherwise route to panel 1 and clobber
    // whatever the user had there in a multi-globe layout.
    if (dataset.runTourOnLoad && gen === this.loadGeneration && !this.tourEngine) {
      const ref = dataset.runTourOnLoad
      try {
        if (ref.startsWith('http://') || ref.startsWith('https://') || ref.endsWith('.json')) {
          logger.info('[App] Auto-starting tour from runTourOnLoad URL:', ref)
          await this.startTour(ref, gen, targetSlot, {
            tourId: dataset.id,
            tourTitle: dataset.title,
            source: 'auto',
          })
        } else {
          const tourDataset = dataService.getDatasetById(ref)
          if (tourDataset && tourDataset.format === 'tour/json') {
            logger.info('[App] Auto-starting tour from runTourOnLoad dataset:', tourDataset.id)
            const tourUrl = tourDataset.tourJsonUrl ?? tourDataset.dataLink
            await this.startTour(tourUrl, gen, targetSlot, {
              tourId: tourDataset.id,
              tourTitle: tourDataset.title,
              source: 'auto',
            })
          } else {
            logger.warn('[App] runTourOnLoad references unknown dataset:', ref)
          }
        }
      } catch (err) {
        logger.warn('[App] runTourOnLoad failed (tour is optional):', err)
      }
    }
  }

  /**
   * Tells the Analyze panel when the globe has stopped on a frame.
   *
   * Reads the transport rather than subscribing to it, because the
   * playback loop below already runs every frame and a second
   * requestAnimationFrame on a page whose performance story is a WebGL
   * globe would be a poor trade for a signal that only matters when
   * nothing is moving. `playbackSettle` owns the decision about what
   * counts as settled; this only supplies the sample.
   *
   * A missing video is reported as `paused` with no playhead — an image
   * dataset has nothing to settle on, and the detector treats that as
   * "forget where you were" rather than as a still frame.
   */
  private readonly playbackSettle = createPlaybackSettleWatcher(
    () => {
      const video = this.hlsService?.getVideo() ?? null
      return video
        ? { playhead: video.currentTime, paused: video.paused, seeking: video.seeking }
        : { playhead: null, paused: true }
    },
    () => {
      notifyAnalyzePlaybackSettled()
      // The transport has stopped on a frame, so every sibling's
      // position is finally stable enough to be worth reading back.
      this.verifySiblingTimes()
    },
  )

  /** Start the requestAnimationFrame playback loop that syncs the scrubber, time label, and auto-loop. */
  private doStartPlaybackLoop(): void {
    startPlaybackLoop(
      this.playback,
      this.hlsService,
      this.videoTexture,
      this.appState,
      (time) => this.updateVideoTimeLabel(time),
      () => this.renderer?.getMap()?.triggerRepaint(),
      // Per-frame sibling drift correction. The primary video is the
      // master clock; siblings are kept in temporal lockstep here
      // rather than free-running on a once-set playbackRate (which
      // integrates duration-imprecision error into visible drift).
      () => {
        this.correctSiblingDrift()
        // Beside the drift correction, never inside it: that method
        // returns early while the primary is paused, and a paused
        // primary is exactly the state an output must be told about.
        this.publishPlaybackMirror()
        // Any time notice describes a settled frame; once the playhead
        // is moving again it describes nothing. correctSiblingDrift has
        // already returned above unless the primary is playing.
        if (this.primaryVideoSyncActive) this.clearSiblingTimeNoticesWhileMoving()
        // Cheap on every frame — a comparison and a subtraction — and it
        // has to be sampled at frame rate to know when the playhead
        // stopped. The loop already treats a throw here as something to
        // log and survive, and the watcher swallows its own.
        this.playbackSettle.tick(performance.now())
      },
    )
  }

  /**
   * Load a dataset on behalf of the tour engine.
   * Unlike loadDataset(), this does NOT stop the active tour and does NOT
   * trigger runTourOnLoad — the tour engine is managing the flow.
   */
  private async loadDatasetForTour(datasetId: string, slot?: number): Promise<void> {
    const targetSlot = slot ?? this.viewports.getPrimaryIndex()
    const isPrimarySlot = targetSlot === this.viewports.getPrimaryIndex()
    const targetRenderer = this.viewports.getRendererAt(targetSlot)
    const tourLoadStartWall = Date.now()
    logger.debug('[App] loadDatasetForTour:', datasetId, 'slot:', targetSlot)

    if (!targetRenderer) {
      logger.warn('[App] Tour loadDataset: slot renderer missing:', targetSlot)
      return
    }

    const dataset = dataService.getDatasetById(datasetId)
    if (!dataset) {
      logger.warn('[App] Tour loadDataset: dataset not found, skipping:', datasetId)
      return
    }

    // Skip re-loading if this specific panel already has this dataset
    if (this.panelStates[targetSlot]?.dataset?.id === datasetId) {
      logger.debug('[App] Tour loadDataset: already loaded in slot, skipping:', datasetId, targetSlot)
      return
    }

    // Primary slot owns the shared playback state — reset it before
    // tearing down the previous stream. Non-primary slot loads don't
    // touch playback at all.
    if (isPrimarySlot) {
      stopPlaybackLoop(this.playback)
      this.appState.isPlaying = false
      resetPlaybackState(this.playback)
    }

    // Tear down the previous video/HLS on THIS slot
    this.cleanupPanelVideo(targetSlot)

    targetRenderer.removeCloudOverlay?.()
    targetRenderer.removeNightLights?.()
    targetRenderer.disableSunLighting?.()

    if (isPrimarySlot) {
      this.appState.currentDataset = dataset
    }
    if (this.panelStates[targetSlot]) {
      this.panelStates[targetSlot].dataset = dataset
    }
    // Register the dataset's attribution as a phantom MapLibre
    // source so Tools → Credits picks it up. See creditsPanel.ts
    // for the design. No-op on non-MapRenderer surfaces.
    {
      const targetRenderer = this.viewports.getRendererAt(targetSlot)
      if (targetRenderer instanceof MapRenderer) {
        targetRenderer.setDatasetCredits(dataset)
      }
    }
    this.infoDisplayOverride = null
    this.renderInfoPanel()
    this.refreshPanelLegends()

    // On small screens, hide the info panel during tours to reduce clutter
    if (window.innerWidth <= 768) {
      document.getElementById('info-panel')?.classList.add('hidden')
    }

    // Show playback controls so users can scrub through time-series data
    const tourLoaderCallbacks = {
      showPlaybackControls: (show: boolean) => this.showPlaybackControls(show),
      showTimeLabel: (show: boolean) => this.showTimeLabel(show),
    }

    // Per-panel loading indicator so the user sees "Loading…" on the
    // target globe instead of a confusing Blue Marble intermediate.
    this.viewports.setPanelLoading(targetSlot, true, `Loading ${dataset.title}\u2026`)

    // The previous dataset in this slot (if any) is about to be
    // superseded by the tour's next step — emit its unload first.
    this.emitLayerUnloadedForSlot(targetSlot, 'tour')

    if (dataService.isImageDataset(dataset)) {
      const img = await loadImageDataset(
        dataset, targetRenderer, this.appState, this.isMobile, tourLoaderCallbacks,
        { isPrimary: isPrimarySlot },
      )
      if (this.panelStates[targetSlot]) {
        this.panelStates[targetSlot].image = img
        this.panelStates[targetSlot].mediaDatasetId = dataset.id
      }
      this.emitLayerLoaded(dataset, targetSlot, 'tour', 'image', Date.now() - tourLoadStartWall)
    } else if (dataService.isVideoDataset(dataset)) {
      // Clear any previously-cached image element for this slot —
      // if the user just switched from an image dataset to a video
      // dataset, the old decoded image is no longer referenced and
      // should be released so its backing bytes can be GC'd.
      if (this.panelStates[targetSlot]) this.panelStates[targetSlot].image = null
      const result = await loadVideoDataset(
        dataset, targetRenderer, this.appState, this.isMobile, this.playback, tourLoaderCallbacks,
        { isPrimary: isPrimarySlot },
      )
      this.storePanelVideoResult(targetSlot, result, dataset.id)
      if (isPrimarySlot) {
        this.attachPrimaryVideoSync()
        this.doStartPlaybackLoop()
      }
      this.emitLayerLoaded(dataset, targetSlot, 'tour', 'hls', Date.now() - tourLoadStartWall)
    }

    this.viewports.setPanelLoading(targetSlot, false)
    this.primeOrbitLegend(dataset)
    // No runTourOnLoad check — the tour engine is in control
  }

  /** Reset the globe to default Earth for a tour — like goHome but keeps UI clean (no browse panel). */
  private async unloadForTour(): Promise<void> {
    await this.unloadAllPanels()
    clearLegendCache()
    this.appState.currentDataset = null
    this.showPlaybackControls(false)
    this.showTimeLabel(false)
    const infoPanel = document.getElementById('info-panel')
    if (infoPanel) {
      infoPanel.classList.add('hidden')
      infoPanel.classList.remove('expanded')
    }

    if (this.renderer) {
      await this.renderer.loadDefaultEarthMaterials()
      const sun = getSunPosition(new Date())
      this.renderer.enableSunLighting(sun.lat, sun.lng)
    }
  }

  /**
   * Fetch a tour JSON file and start the tour engine.
   *
   * @param anchorSlot  When set, the tour is scoped to a specific
   *   panel — `loadDataset` tasks route there and the legacy SOS
   *   `worldIndex: 1` convention ("the current globe") is treated
   *   as "the anchored globe." Passed by the `runTourOnLoad` flow
   *   so a chained tour can't clobber another panel's dataset.
   */
  private async startTour(
    dataLink: string,
    gen: number,
    anchorSlot: number | null = null,
    meta?: TourTelemetryMeta,
  ): Promise<void> {
    // Stop any previous tour
    this.stopTour()

    const resp = await fetch(dataLink)
    if (!resp.ok) throw new Error(`Failed to fetch tour: ${resp.status}`)
    const tourFile: TourFile = await resp.json()

    if (gen !== this.loadGeneration) return

    // Use the final response URL as the base for resolving relative media paths.
    // This handles redirects and ensures relative URLs work even when dataLink
    // is a relative path (e.g. /assets/test-tour.json).
    const tourBaseUrl = resp.url || new URL(dataLink, window.location.href).toString()
    this.runTourFromFile(tourFile, tourBaseUrl, anchorSlot, meta)
  }

  /**
   * Phase 3pt-review/J — shared engine + chrome startup, factored
   * out of `startTour` so `playInlineTour` can skip the fetch
   * round-trip. The original blob-URL approach tripped CSP in
   * Firefox (the page can't `fetch()` a `blob:` of itself when
   * `connect-src` doesn't list blob:), so preview now hands the
   * in-memory TourFile straight to the engine. Standalone tours
   * (`startTour` from a URL) still go through the fetch path so
   * `tourBaseUrl` is the real response URL for relative-media-
   * path resolution; preview passes `window.location.href` since
   * draft tours shouldn't carry relative paths.
   */
  private runTourFromFile(
    tourFile: TourFile,
    tourBaseUrl: string,
    anchorSlot: number | null = null,
    meta?: TourTelemetryMeta,
  ): void {
    this.tourEngine = new TourEngine(tourFile, {
      loadDataset: async (id, opts) => {
        await this.loadDatasetForTour(id, opts?.slot)
      },
      unloadAllDatasets: async () => {
        await this.unloadForTour()
      },
      unloadDatasetAt: async (slot) => {
        await this.unloadPanelDataset(slot)
      },
      setEnvView: async ({ layout }) => {
        this.viewports.setLayout(layout, 'tour')
      },
      getRenderer: () => this.renderer!,
      getAllRenderers: () => this.viewports.getAll(),
      getPrimarySlot: () => this.viewports.getPrimaryIndex(),
      togglePlayPause: () => {
        togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m))
      },
      isPlaying: () => this.appState.isPlaying,
      setPlaybackRate: (rate) => {
        if (this.hlsService) this.hlsService.playbackRate = rate
      },
      // What the loaded dataset advances at, so the `frameRate` task
      // divides by the right thing. The rate is already baked into the
      // file — this only tells the tour what it is.
      getPlaybackFps: () => this.appState.currentDataset?.playbackFps,
      // The `setTime` task — same seek the docent's set_time action and
      // the In-the-news jump use. Best-effort: an unseekable dataset or
      // out-of-range time is a quiet no-op mid-tour.
      setTime: (isoTime) => {
        seekToDate(isoTime, this.hlsService, this.appState, this.playback)
      },
      onTourEnd: () => this.endTour(),
      onStop: () => {
        // User-initiated stop. Release a playlist that was waiting
        // for this tour to finish so it advances on the per-entry
        // timer rather than hanging in waitingForTour state.
        this.stopTour()
        notifyPlaylistTourEnded()
      },
      announce: (msg) => this.announce(msg),
      resolveMediaUrl: (filename) => {
        try {
          return new URL(filename, tourBaseUrl).toString()
        } catch {
          return filename
        }
      },
    }, { anchorSlot, meta })

    showTourControls(this.tourEngine, () => {
      this.stopTour()
      notifyPlaylistTourEnded()
    })
    this.showPlaybackControls(false)
    hideBrowseUI()
    closeChat()
    document.body.classList.add('tour-active')

    // On small screens, hide non-essential UI and shift globe up
    if (window.innerWidth <= 768) {
      document.getElementById('map-controls')?.classList.add('hidden')
      document.getElementById('info-panel')?.classList.add('hidden')
    }

    void this.tourEngine.play()
  }

  /** Called when the tour finishes naturally (not via stop button). */
  private endTour(): void {
    const wasStandalone = this.tourIsStandalone
    this.cleanupTourOverlays()
    this.tourEngine = null
    this.tourIsStandalone = false
    this.announce('Tour ended')

    if (wasStandalone) {
      // Standalone tour (tour/json dataset) — return home so user isn't stuck
      void this.goHome()
    } else {
      // runTourOnLoad tour — stay on the current dataset, restore playback UI
      this.restorePostTourUI()
    }
    // Playlist auto-advance — if a playlist entry was waiting for
    // the tour to finish, advance to the next entry. No-op when
    // the tour wasn't triggered from a playlist.
    notifyPlaylistTourEnded()
  }

  /** Stop any active tour without triggering goHome. */
  private stopTour(): void {
    if (this.tourEngine) {
      this.tourEngine.stop()
      this.tourEngine = null
      this.tourIsStandalone = false
      this.cleanupTourOverlays()
      this.restorePostTourUI()
    }
  }

  /** Restore playback/time UI after a tour ends, based on the current dataset. */
  private restorePostTourUI(): void {
    const dataset = this.appState.currentDataset
    if (dataset && dataService.isVideoDataset(dataset)) {
      this.showPlaybackControls(true)
      if (this.hlsService) {
        updatePlayButton(this.hlsService.paused)
      }
    }
    // Restore UI hidden during tour
    document.getElementById('map-controls')?.classList.remove('hidden')
    document.body.classList.remove('tour-active')
  }

  /** Remove all tour UI elements. */
  private cleanupTourOverlays(): void {
    hideTourControls()
    hideAllTourTextBoxes()
    hideAllTourImages()
    hideAllTourVideos()
    hideAllTourPopups()
    hideAllTourQuestions()
  }

  /** Map the current video playback time to a real-world date and update the time label. */
  private updateVideoTimeLabel(videoTime: number): void {
    const dataset = this.appState.currentDataset
    if (!dataset) {
      this.assertedLabelDate = null
      return
    }

    if (dataset.startTime && dataset.endTime) {
      const start = new Date(dataset.startTime)
      const end = new Date(dataset.endTime)
      const videoDuration = this.hlsService?.duration ?? 1
      const snapMs = this.playback.displayInterval?.intervalMs
      const currentDate = videoTimeToDate(videoTime, videoDuration, start, end, snapMs)
      // The same playhead without the snap. `verifySiblingTimes` compares
      // true instants across panels; snapping first would make a
      // sub-frame difference at a bucket edge look like a full step.
      const trueDate = videoTimeToDate(videoTime, videoDuration, start, end)
      const showTime = dataset.period
        ? isSubDailyPeriod(dataset.period)
        : (this.playback.displayInterval?.showTime ?? false)
      this.appState.timeLabel = formatDate(currentDate, showTime)
      this.assertedLabelDate = trueDate
      this.showTimeLabel(true)
    } else {
      this.assertedLabelDate = null
      this.showTimeLabel(false)
    }
  }

  /** Show or hide the time label overlay and update its text from app state. */
  private showTimeLabel(show: boolean): void {
    const timeLabel = document.getElementById('time-label')
    const timeDisplay = document.getElementById('time-display')
    if (timeLabel && timeDisplay) {
      if (show) {
        timeDisplay.textContent = this.appState.timeLabel
        timeLabel.classList.remove('hidden')
        const scrubber = document.getElementById('scrubber')
        if (scrubber) scrubber.setAttribute('aria-valuetext', this.appState.timeLabel)
      } else {
        timeLabel.classList.add('hidden')
      }
    }
  }

  // --- UI helpers ---

  /** Toggle visibility of the playback transport controls. */
  private showPlaybackControls(show: boolean): void {
    const controls = document.getElementById('playback-controls')
    if (controls) {
      controls.classList.toggle('hidden', !show)
    }
    updateMapControlsPosition()
  }

  /**
   * Toggle dataset info panel visibility. Persists the choice and
   * updates the DOM immediately. When the info panel is the only
   * place showing the legend (single-view with legend prefs on), the
   * legend moves to a floating element in the primary panel.
   */
  private setDatasetInfoVisible(visible: boolean): void {
    this.viewPrefs.infoPanelVisible = visible
    saveViewPreferences(this.viewPrefs)
    this.applyDatasetInfoVisibility()
    this.refreshPanelLegends()
  }

  /** Toggle legend visibility. Persists and refreshes per-panel legends. */
  private setLegendVisible(visible: boolean): void {
    this.viewPrefs.legendVisible = visible
    saveViewPreferences(this.viewPrefs)
    this.refreshPanelLegends()
    // Also hide/show the in-info-panel legend thumbnail since it
    // lives inside the info panel body.
    const legendThumb = document.querySelector('.info-legend-thumb') as HTMLElement | null
    if (legendThumb) {
      legendThumb.style.display = visible ? '' : 'none'
    }
  }

  /** Apply the current infoPanelVisible preference to the DOM. */
  private applyDatasetInfoVisibility(): void {
    const panel = document.getElementById('info-panel')
    if (!panel) return
    const hasDataset = !!this.appState.currentDataset
    // When no dataset is loaded, the info panel is hidden regardless —
    // the preference only matters when there's content to show.
    if (!hasDataset) {
      panel.classList.add('hidden')
      return
    }
    panel.classList.toggle('hidden', !this.viewPrefs.infoPanelVisible)
  }

  /**
   * Which slot's dataset the info panel is currently displaying —
   * the override if set, otherwise the primary. Returns -1 if no
   * panel has a loaded dataset.
   */
  private getInfoDisplayIndex(): number {
    if (
      this.infoDisplayOverride !== null &&
      this.panelStates[this.infoDisplayOverride]?.dataset
    ) {
      return this.infoDisplayOverride
    }
    const primaryIdx = this.viewports.getPrimaryIndex()
    if (this.panelStates[primaryIdx]?.dataset) return primaryIdx
    // Fall back to the first slot with a loaded dataset so the info
    // panel isn't blank when the primary happens to be empty.
    for (let i = 0; i < this.panelStates.length; i++) {
      if (this.panelStates[i]?.dataset) return i
    }
    return -1
  }

  /**
   * Fly the primary globe to a related current event's place and seek the
   * loaded dataset to its time — the "In the news" card's "View on globe"
   * action (`docs/CURRENT_EVENTS_PLAN.md` §6). Reuses the same primitives
   * Orbit's docent drives: `renderer.flyTo` / `fitBounds` for place,
   * `seekToDate` for time. Point geometry flies in to a regional altitude;
   * a bounding box or a named region fits its extent. The time seek only
   * runs when the event's start falls inside the loaded dataset's coverage;
   * outside it (common for rolling real-time windows) we still fly to the
   * place and report `out-of-range` so the card can note it.
   */
  private navigateToEvent(ev: PublicEvent): EventNavResult {
    // A point event flies to ~regional altitude (km → MapLibre zoom in
    // `MapRenderer.flyTo`); box/region events fit their extent instead.
    const EVENT_FLY_ALTITUDE_KM = 3000
    let navigated = false
    const g = ev.geometry
    if (g.point) {
      void this.renderer?.flyTo(g.point.lat, g.point.lon, EVENT_FLY_ALTITUDE_KM)
      if (isVrActive()) void flyToOnGlobe(g.point.lat, g.point.lon)
      navigated = true
    } else if (g.boundingBox) {
      const { n, s, w, e } = g.boundingBox
      this.renderer?.fitBounds([w, s, e, n])
      navigated = true
    } else if (g.regionName) {
      const region = resolveRegion(g.regionName)
      if (region) {
        this.renderer?.fitBounds(region.bounds)
        navigated = true
      }
    }

    let time: EventNavResult['time'] = 'none'
    if (ev.occurredStart) {
      const ds = this.appState.currentDataset
      const start = ds?.startTime ? new Date(ds.startTime).getTime() : NaN
      const end = ds?.endTime ? new Date(ds.endTime).getTime() : NaN
      const target = new Date(ev.occurredStart).getTime()
      // Only temporal datasets can be seeked or reported out-of-range; a
      // static image has no time axis, so there is nothing to note.
      if (!isNaN(start) && !isNaN(end) && end > start && !isNaN(target)) {
        if (target >= start && target <= end) {
          seekToDate(ev.occurredStart, this.hlsService, this.appState, this.playback)
          time = 'seeked'
        } else {
          time = 'out-of-range'
        }
      }
    }
    return { navigated, time }
  }

  /**
   * Render the info panel for whichever slot `getInfoDisplayIndex`
   * points at, repopulate the picker dropdown with all loaded
   * datasets, and show/hide the picker based on how many are loaded.
   *
   * Call after: dataset load/unload, primary change, layout change,
   * info-panel-visibility toggle. Idempotent — safe to call
   * repeatedly.
   */
  private renderInfoPanel(): void {
    const idx = this.getInfoDisplayIndex()
    const dataset = idx >= 0 ? this.panelStates[idx]?.dataset ?? null : null

    if (!dataset) {
      // Nothing to show — hide the whole panel.
      document.getElementById('info-panel')?.classList.add('hidden')
      return
    }

    // Render the currently-selected dataset into the info panel body.
    displayDatasetInfo(
      dataset,
      this.appState.datasets,
      (id) => this.loadDataset(id, 'browse'),
      (ev) => this.navigateToEvent(ev),
    )

    // Repopulate the picker with every loaded dataset (in panel order)
    // and wire the change handler once.
    const picker = document.getElementById('info-panel-picker')
    const select = document.getElementById('info-selector') as HTMLSelectElement | null
    if (!picker || !select) return

    const entries: Array<{ slot: number; label: string }> = []
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const d = this.panelStates[slot]?.dataset
      if (!d) continue
      const label = this.panelStates.length > 1
        ? `Panel ${slot + 1}: ${d.title}`
        : d.title
      entries.push({ slot, label })
    }

    // Only show the picker when there's a choice to make.
    if (entries.length > 1) {
      picker.classList.remove('hidden')
      // Rebuild options only if the set changed — a naive innerHTML
      // rewrite would lose focus if the user is mid-interaction.
      const wantSignature = entries.map(e => `${e.slot}:${e.label}`).join('|')
      if (select.dataset.signature !== wantSignature) {
        select.innerHTML = ''
        for (const e of entries) {
          const opt = document.createElement('option')
          opt.value = String(e.slot)
          opt.textContent = e.label
          select.appendChild(opt)
        }
        select.dataset.signature = wantSignature
      }
      select.value = String(idx)
    } else {
      picker.classList.add('hidden')
    }

    if (!this.infoSelectorWired) {
      this.infoSelectorWired = true
      select.addEventListener('click', (ev) => { ev.stopPropagation() })
      select.addEventListener('change', () => {
        const newSlot = parseInt(select.value, 10)
        if (Number.isFinite(newSlot)) {
          this.infoDisplayOverride = newSlot
          this.renderInfoPanel()
        }
      })
    }

    // Now that the body has content, apply the user's visibility
    // preference — the panel may still be hidden if they toggled it
    // off in Tools.
    this.applyDatasetInfoVisibility()
  }

  /**
   * Rebuild the floating per-panel legends from the current
   * panelStates array + view prefs. Called after any change that
   * affects whether legends should be visible or which dataset each
   * panel is showing:
   *
   * - Legend toggle flipped
   * - Info panel toggle flipped (legend may move in/out of the info panel)
   * - A dataset loaded/unloaded in any panel
   * - Layout changed (panels added/removed)
   * - Primary changed (floating legend vs info-panel thumbnail split)
   */
  private refreshPanelLegends(): void {
    const legendOn = this.viewPrefs.legendVisible
    const infoOn = this.viewPrefs.infoPanelVisible
    const isMultiView = this.viewports.getPanelCount() > 1
    const primaryIdx = this.viewports.getPrimaryIndex()
    let anyColorbar = false

    for (let slot = 0; slot < this.panelStates.length; slot++) {
      const panel = this.panelStates[slot]
      const dataset = panel?.dataset ?? null
      const legendLink = dataset?.legendLink ?? null

      // Decide whether this panel gets a floating legend:
      // - Off entirely if the Legend toggle is off
      // - Off if the panel has no dataset or no legendLink
      // - Off for the primary in single-view mode when the info
      //   panel is visible (the info panel holds the legend there)
      // - On otherwise
      // A data-encoded row carries its exact palette, range and units,
      // so the rendered colorbar supersedes the uploaded legend image —
      // which for these datasets describes at best the same thing and
      // at worst a previous encode.
      const scale = dataset?.renderEncoding === RENDER_ENCODING_DATA_LUMA
        ? dataset.colorScale ?? null
        : null

      let showFloating = legendOn && (!!legendLink || !!scale)
      if (showFloating && !isMultiView && slot === primaryIdx && infoOn && !scale) {
        showFloating = false
      }

      if (showFloating && scale && dataset) {
        this.viewports.setPanelLegend(slot, null)
        this.viewports.setPanelColorbar(slot, renderColorbar({
          scale,
          display: this.colorScaleDisplay,
          title: dataset.title,
          onOpen: () => this.openColorbarControls(scale),
        }))
        anyColorbar = true
        continue
      }
      this.viewports.setPanelColorbar(slot, null)

      if (showFloating && legendLink && dataset) {
        this.viewports.setPanelLegend(slot, legendLink, {
          title: dataset.title,
          onClick: () => this.openLegendModal(legendLink, dataset.title),
        })
      } else {
        this.viewports.setPanelLegend(slot, null)
      }
    }

    // Nothing on screen carries a palette any more — the dataset was
    // unloaded or swapped for a picture — so an open controls popover
    // is adjusting the colours of nothing, and an open Analyze panel is
    // showing statistics for a frame that is no longer displayed.
    if (!anyColorbar) {
      closeDisplayControls()
      closeAnalyzeUI()
    } else {
      // Still something to colour, but possibly a different dataset —
      // the Analyze panel's numbers belong to the one it opened on.
      notifyAnalyzeDatasetChanged(this.appState.currentDataset?.id ?? null)
    }
  }

  /**
   * Open the palette / range / threshold controls for a data-encoded
   * dataset, and fan every change out to all panels.
   *
   * The transform goes to every globe rather than the one that was
   * tapped: in a 2- or 4-globe layout the panels exist to be compared,
   * and comparing two fields through two different palettes is worse
   * than not comparing them.
   */
  private openColorbarControls(scale: NonNullable<Dataset['colorScale']>): void {
    openDisplayControls({
      scale,
      display: this.colorScaleDisplay,
      // Read the frame once, when the controls open, so the sliders
      // are placed on the data's own distribution rather than on the
      // palette's nominal range — see `DisplayControlsOptions`.
      // Null whenever the frame isn't readable, which falls back to
      // the linear placement rather than failing.
      distribution: () => {
        const frame = this.viewports.getPrimary()?.analysisFrame()
        if (!frame) return null
        return buildHistogram(frame.snapshot, frame.scale, frame.options).weights
      },
      onChange: (next) => {
        this.colorScaleDisplay = next
        this.viewports.setColorScaleDisplay(next)
        // The one place this value changes, so the one place an output
        // can learn about it. It is deliberately *not* republished on a
        // dataset load: the transform is app-wide and outlives the
        // dataset it was set on, exactly as it does on the control
        // globes, and a picture dataset ignores it anyway because
        // `paletteTexture` builds no LUT without a `colorScale`.
        publishGlobeState({ display: displayForMirror(next) })
        // Rebuild the floating bars so they track the globe. Cheap:
        // this is DOM, and the LUT upload has already happened.
        this.refreshPanelLegends()
      },
    })
  }

  /** Open the full-size legend modal for a dataset. Mirrors the
   *  legend click handler inside datasetLoader's info panel so the
   *  floating legends and the info-panel thumbnail use the same UI. */
  private openLegendModal(src: string, title: string): void {
    let overlay = document.getElementById('legend-modal-overlay')
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.id = 'legend-modal-overlay'
      overlay.className = 'legend-modal-overlay'
      overlay.setAttribute('role', 'dialog')
      overlay.setAttribute('aria-modal', 'true')
      overlay.innerHTML = `<img class="legend-modal-img" alt="Legend"><button class="legend-modal-close" aria-label="Close legend">&times;</button>`
      const closeModal = () => overlay!.classList.add('hidden')
      overlay.querySelector('.legend-modal-close')!.addEventListener('click', (e) => {
        e.stopPropagation()
        closeModal()
      })
      overlay.addEventListener('click', closeModal)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !overlay!.classList.contains('hidden')) closeModal()
      })
      document.body.appendChild(overlay)
    }
    const img = overlay.querySelector('img') as HTMLImageElement
    img.src = src
    img.alt = `${title} legend`
    overlay.setAttribute('aria-label', `${title} legend`)
    overlay.classList.remove('hidden')
  }

  private dismissBrowseAfterLoad(): void {
    // Multi-viewport mode keeps the browse panel around so the
    // visitor can load datasets into the remaining panels.
    // Catalog mode (`?catalog=true`) keeps it around for the same
    // reason — the catalog↔sphere tab control treats the
    // collapsed browse panel as the "back to catalog" affordance.
    // Single-viewport sphere mode hides the panel outright.
    if (this.viewports.getPanelCount() > 1 || getCatalogMode()) {
      collapseBrowseUI()
    } else {
      hideBrowseUI()
    }
  }

  /**
   * Open (or re-open) the dataset browse panel. Called by the
   * Browse button in the map-controls toolbar. Handles all three
   * states the panel can be in: hidden (initial), collapsed (after
   * a previous multi-view load), or already visible (no-op).
   */
  private openBrowsePanel(): void {
    const overlay = document.getElementById('browse-overlay')
    // Only call showBrowseUI once — it wires event listeners on
    // category chips, search input, etc. Re-calling it after a
    // hideBrowseUI() would duplicate those listeners.
    if (!overlay || overlay.dataset.browseInitialized !== 'true') {
      showBrowseUI(this.appState.datasets, {
        onSelectDataset: (id) => this.selectDatasetFromBrowse(id),
        announce: (msg) => this.announce(msg),
        isMobile: this.isMobile,
        onOpenChat: this.browseOpenChat(),
      })
      // Mark as initialized so subsequent opens skip showBrowseUI.
      const el = overlay ?? document.getElementById('browse-overlay')
      if (el) el.dataset.browseInitialized = 'true'
      return
    }
    // Already rendered — either collapsed or fully visible. Remove
    // the collapsed class either way and ensure `browse-open` is
    // set so other UI can react.
    const wasCollapsed = overlay.classList.contains('collapsed')
    const wasHidden = overlay.classList.contains('hidden')
    overlay.classList.remove('collapsed')
    overlay.classList.remove('hidden')
    document.body.classList.add('browse-open')
    // Re-opening from collapsed (or hidden, in pathological cases
    // where showBrowseUI ran but the overlay got hidden externally)
    // is conceptually a fresh open — emit browse_opened and
    // restart the dwell handle. The collapseBrowseUI path stops
    // dwell, so this pairs with that.
    if (wasCollapsed || wasHidden) {
      notifyBrowseOpened('tools')
    }
  }

  /**
   * Catalog tab clicked. Re-add `?catalog=true` to the URL (if it
   * was dropped by a previous Sphere click), expand the browse
   * panel, and flip the tab to Catalog-active. Body class
   * `catalog-mode` is sticky for the session — this handler just
   * re-asserts it in case something else cleared it.
   */
  private enterCatalogTab(): void {
    if (!getCatalogMode()) setCatalogMode(true)
    document.body.classList.add('catalog-mode')
    if (!this.appState.currentDataset) {
      document.body.classList.add('catalog-empty')
    }
    this.openBrowsePanel()
    setActiveCatalogTab('catalog')
  }

  /**
   * Sphere tab clicked. Drop `?catalog=true` from the URL,
   * collapse (or hide, when no dataset is loaded) the browse
   * panel, and flip the tab to Sphere-active. The
   * `catalog-mode` body class stays set so the tab control
   * remains visible — Plan §3.2's "shared MapRenderer between
   * tabs" pattern means we never tear catalog mode down within
   * a session, only on a fresh page load.
   */
  private enterSphereTab(): void {
    if (getCatalogMode()) setCatalogMode(false)
    document.body.classList.remove('catalog-empty')
    if (this.appState.currentDataset) {
      collapseBrowseUI()
    } else {
      hideBrowseUI()
    }
    setActiveCatalogTab('sphere')
  }

  /**
   * Wire a popstate handler so browser back/forward syncs the
   * catalog-mode UI with whatever the new URL says. Dataset
   * loads on popstate are out of scope (a pre-existing gap in
   * the app, not specific to catalog mode); the visitor sees
   * the URL flip and the browse-panel/tab visibility update but
   * the loaded dataset doesn't change without a refresh.
   */
  private wireCatalogModePopstate(): void {
    window.addEventListener('popstate', () => {
      const onCatalog = getCatalogMode()
      const datasetLoaded = this.appState.currentDataset !== null
      if (onCatalog) {
        document.body.classList.add('catalog-mode')
        showCatalogTabs()
        if (datasetLoaded) {
          // `?catalog=true&dataset=<id>` — case 2 from §3.1:
          // globe visible, browse panel collapsed as the "back to
          // catalog" affordance, Sphere tab active. Mirrors the
          // boot-path branch that handles the same URL shape.
          document.body.classList.remove('catalog-empty')
          collapseBrowseUI()
          setActiveCatalogTab('sphere')
        } else {
          // `?catalog=true` (no dataset) — case 1: browse panel
          // full-surface, globe hidden, Catalog tab active.
          document.body.classList.add('catalog-empty')
          this.openBrowsePanel()
          setActiveCatalogTab('catalog')
        }
      } else {
        document.body.classList.remove('catalog-empty')
        if (document.body.classList.contains('catalog-mode')) {
          // Sticky body class means tabs stay visible — just flip
          // active to Sphere.
          setActiveCatalogTab('sphere')
          if (datasetLoaded) {
            collapseBrowseUI()
          } else {
            hideBrowseUI()
          }
        } else {
          hideCatalogTabs()
        }
      }
    })
  }

  /** Detect WebGL 2 support. If unavailable, display troubleshooting instructions and return false. */
  private checkWebGLSupport(): boolean {
    // The predicate, and why it is WebGL 2 with no WebGL 1 fallback, lives
    // in `utils/webglSupport` — reachable from a test, which this method is
    // not. What stays here is the screen that follows from a `false`.
    if (hasWebGL2()) return true

    const screen = document.getElementById('loading-screen')
    if (screen) {
      screen.innerHTML = `
        <div style="max-width:480px;padding:2rem;text-align:center;color:#e0e0e0;font-family:system-ui,sans-serif;">
          <div style="font-size:2.5rem;margin-bottom:0.75rem;" aria-hidden="true">&#x1F30D;</div>
          <h1 style="font-size:1.1rem;margin:0 0 0.75rem;color:#fff;">WebGL 2 is not available</h1>
          <p style="font-size:0.8rem;line-height:1.5;color:#aaa;margin:0 0 1.25rem;">
            This application requires WebGL 2 to render the interactive globe.
            Your browser's GPU acceleration appears to be disabled.
          </p>
          <details style="text-align:start;font-size:0.75rem;color:#999;line-height:1.6;">
            <summary style="cursor:pointer;color:#4da6ff;margin-bottom:0.5rem;">How to fix this</summary>
            <ol style="padding-inline-start:1.25rem;margin:0;">
              <li>Open <strong style="color:#fff;">chrome://flags</strong> in your address bar</li>
              <li>Search for <strong style="color:#fff;">Override software rendering list</strong></li>
              <li>Set it to <strong style="color:#fff;">Enabled</strong> and relaunch Chrome</li>
            </ol>
            <p style="margin:0.75rem 0 0.25rem;color:#888;">Alternatively, launch Chrome from the terminal with:</p>
            <code style="display:block;background:#1a1a2e;padding:0.5rem 0.75rem;border-radius:4px;color:#4da6ff;font-size:0.7rem;overflow-x:auto;">
              google-chrome --ignore-gpu-blocklist --enable-unsafe-swiftshader
            </code>
            <p style="margin:0.75rem 0 0;color:#888;">
              If the problem persists, check <strong style="color:#fff;">chrome://gpu</strong>
              for driver issues. Installing or updating your GPU drivers
              (e.g. <code style="color:#ccc;">sudo apt install nvidia-driver-xxx</code>
              or <code style="color:#ccc;">mesa-utils</code>) usually resolves this.
            </p>
          </details>
        </div>`
      screen.style.display = 'flex'
      screen.style.alignItems = 'center'
      screen.style.justifyContent = 'center'
      screen.classList.remove('fade-out')
    }
    return false
  }

  /** Set the loading state. When false, fades out the loading screen with a short delay. */
  private setLoading(isLoading: boolean): void {
    this.appState.isLoading = isLoading
    if (!isLoading) {
      const screen = document.getElementById('loading-screen')
      if (screen) {
        screen.setAttribute('aria-busy', 'false')
        this.setLoadingStatus('Ready', 100)
        this.loadingHideTimer = setTimeout(() => {
          this.loadingHideTimer = null
          screen.style.opacity = ''
          screen.classList.add('fade-out')
          // The screen's own fade only: `transitionend` bubbles, and the
          // progress fill and the tagline transition inside it too, so
          // one of theirs would otherwise end the fade early.
          const onFaded = (e: TransitionEvent): void => {
            if (e.target !== screen) return
            screen.removeEventListener('transitionend', onFaded)
            if (screen.classList.contains('fade-out')) {
              screen.style.display = 'none'
            }
          }
          screen.addEventListener('transitionend', onFaded)
        }, LOADING_HIDE_DELAY_MS)
      }
    }
  }

  /** Update the loading screen status message and progress bar. */
  private setLoadingStatus(message: string, progress?: number): void {
    const statusEl = document.getElementById('loading-status')
    if (statusEl) statusEl.textContent = message
    if (progress !== undefined) {
      const track = document.querySelector('.loading-progress-track')
      if (track) track.setAttribute('aria-valuenow', String(Math.round(progress)))
      const fill = document.getElementById('loading-progress-fill')
      if (fill) (fill as HTMLElement).style.width = `${progress}%`
    }
  }

  /** Display an error message in the error banner and log it. */
  private setError(error: string): void {
    this.appState.error = error
    const errorEl = document.getElementById('error-message')
    if (errorEl) {
      const textEl = document.getElementById('error-text')
      if (textEl) textEl.textContent = error
      errorEl.classList.toggle('hidden', !error)
      const dismissBtn = document.getElementById('error-dismiss')
      if (dismissBtn) {
        dismissBtn.onclick = () => {
          errorEl.classList.add('hidden')
          this.appState.error = null
        }
      }
    }
    logger.error('[App] Error:', error)
  }

  /** Push a message to the ARIA live region for screen reader announcements. */
  private announce(message: string): void {
    const el = document.getElementById('a11y-announcer')
    if (el) {
      el.textContent = ''
      requestAnimationFrame(() => { el.textContent = message })
    }
  }

  /**
   * Publish the primary panel's dataset to any multi-monitor outputs
   * (`docs/MULTI_MONITOR_PLAN.md` §3).
   *
   * Reads current state rather than taking the changed slot, and is
   * called from every path that can change *which dataset the primary
   * is showing* — a load, an unload, and panel promotion, which changes
   * it without either. Over-calling is free: the aggregator drops a
   * patch whose value is structurally identical, so a load into a
   * non-primary slot costs one comparison and sends nothing. Missing a
   * call is the only failure with a cost, which is why this reads the
   * world instead of being told about it.
   *
   * The URL is the one *this* window resolved — after offline-cache
   * lookup and variant probing — because the protocol makes that the
   * control window's job. It cannot be read back off the media element:
   * on the hls.js path `video.src` is a `blob:` MediaSource handle.
   *
   * **`dataset` alone is not what the panel is showing.** It is assigned
   * before the load is attempted, so it stays set when a load fails,
   * while one is in flight, and for a `tour/json` row that paints
   * nothing at all — in each case the panel still holds the *previous*
   * dataset's pixels. Publishing from `dataset` and `image` together
   * without checking they agree is how an output ends up rendering one
   * dataset's texture under another's bbox, `lonOrigin`, flip and
   * palette, labelled with the wrong title. `mediaDatasetId` records
   * which dataset the media actually belongs to, and the three cases
   * below are what that comparison yields.
   */
  private publishMirroredDataset(): void {
    const panel = this.panelStates[this.viewports.getPrimaryIndex()]
    const dataset = panel?.dataset ?? null
    const settled = panelMirrorState(dataset?.id, panel?.mediaDatasetId ?? null)

    // Genuinely empty — the panel is back to the default Earth, and an
    // output should follow it there.
    if (!panel || settled === 'empty') {
      // The playhead goes with it. `publishPlaybackMirror` would
      // eventually say the same thing, but only while the playback loop
      // is running — it is stopped on unload, so without this an output
      // keeps the departed dataset's instant and `outputSync` steers a
      // clip that is no longer on screen.
      publishGlobeState({ dataset: null, playback: null, primary: null })
      return
    }

    // Row and pixels disagree: a load failed or is still in flight, a
    // teardown is half-done, or the primary holds a tour row whose
    // script has not loaded anything yet. Publish NOTHING rather than
    // guessing. `null` would blank the sphere while the operator is
    // still looking at the old dataset, and the row would mislabel the
    // old pixels — so the honest move is to leave the output showing
    // what it has until the panel settles, which fires this again.
    if (settled === 'unsettled' || !dataset) return

    const kind = dataService.isVideoDataset(dataset)
      ? 'video'
      : dataService.isImageDataset(dataset)
        ? 'image'
        : null
    // Unreachable while `mediaDatasetId` is only set beside real pixels
    // (a tour row never paints), but a format that mirrors as neither
    // kind must not be guessed at either.
    if (kind === null) return

    const url = kind === 'video'
      ? panel.hlsService?.getSourceUrl() ?? null
      : panel.image?.src ?? null
    publishGlobeState({ dataset: toMirroredDataset(dataset, kind, url) })
  }

  /** Emit a `layer_loaded` event and remember when the slot filled so
   * the matching `layer_unloaded` can report dwell_ms. */
  private emitLayerLoaded(
    dataset: Dataset,
    slot: number,
    trigger: import('./types').LoadTrigger,
    source: import('./types').LayerSource,
    loadMs: number,
  ): void {
    const now = Date.now()
    if (this.panelStates[slot]) {
      this.panelStates[slot].loadedAt = now
    }
    emit({
      event_type: 'layer_loaded',
      layer_id: dataset.id,
      layer_source: source,
      slot_index: String(slot),
      trigger,
      load_ms: Math.max(0, Math.round(loadMs)),
    })
    // Both load paths funnel through here, after the panel has been
    // given its image / HLS service — so this is the first point at
    // which the resolved URL an output needs actually exists.
    this.publishMirroredDataset()
  }

  /** Emit `layer_unloaded` for whatever dataset currently occupies
   * `slot`, if any. No-op when the slot is empty. Clears the
   * `loadedAt` timestamp so repeat unloads don't double-count. */
  private emitLayerUnloadedForSlot(
    slot: number,
    reason: import('./types').UnloadReason,
  ): void {
    const panel = this.panelStates[slot]
    if (!panel || !panel.dataset) return
    const loadedAt = panel.loadedAt ?? Date.now()
    emit({
      event_type: 'layer_unloaded',
      layer_id: panel.dataset.id,
      slot_index: String(slot),
      reason,
      dwell_ms: Math.max(0, Date.now() - loadedAt),
    })
    panel.loadedAt = null
  }

  /** Emit `playback_action` for a transport-control event. Reads the
   * current video's time and rate so downstream queries can see how
   * far into a time-series users actually scrub. */
  private emitPlaybackAction(action: 'play' | 'pause' | 'seek' | 'rate'): void {
    const layer_id = this.appState.currentDataset?.id ?? 'unknown'
    const video = this.hlsService?.getVideo?.()
    emit({
      event_type: 'playback_action',
      layer_id,
      action,
      playback_time_s: video?.currentTime ?? 0,
      playback_rate: video?.playbackRate ?? 1,
    })
  }

  /**
   * Wire the Enter VR button. The button hides itself on browsers
   * without WebXR, so calling this unconditionally is safe — the
   * Three.js chunk only downloads on devices that advertise
   * `immersive-vr` support (see src/ui/vrButton.ts). VR mode always
   * uses the primary panel's dataset; in multi-globe layouts the
   * other panels keep rendering in 2D behind the scenes.
   *
   * Both image and video datasets are supported: image datasets
   * reuse the primary panel's already-decoded `HTMLImageElement`
   * (stored in `panelStates[slot].image` after `loadImageDataset`
   * returns), so VR doesn't re-fetch or re-decode the bytes;
   * video datasets reuse the existing HLS `<video>` element.
   */
  private async wireVrButton(): Promise<void> {
    // Shared helper — returns the texture spec for a specific
    // panel slot by looking at that slot's own panel state (its
    // dataset + HLS service + decoded image). This is the
    // multi-panel foundation; the single-panel getters below are
    // just convenience wrappers that call it for the primary slot.
    // Per-slot memoization. `vrSession` polls `getPanelTexture` at
    // XR frame rate (~90 Hz on Quest); recomputing the spec —
    // including `overlayOptionsFromDataset` which allocates a fresh
    // options object for datasets with any Phase 3d field set —
    // would burn ~5400 allocations/min for no behavioural reason.
    // Cache on input-reference identity: as long as the panel's
    // dataset, image, and video references are stable, the spec is
    // the same object. Dataset metadata mutated in place would
    // bypass this cache; the codebase replaces dataset objects on
    // load rather than mutating, and `photorealEarth.setTexture`
    // re-applies overlay options on every call regardless of
    // memoization so a stale spec is at most one frame visible.
    const specCache: Array<{
      dataset: Dataset | null
      image: HTMLImageElement | null
      video: HTMLVideoElement | null
      spec: VrDatasetTexture | null
    } | undefined> = []
    const getPanelTexture = (slot: number): VrDatasetTexture | null => {
      const panel = this.panelStates[slot]
      const dataset = panel?.dataset ?? null
      const image = dataset && dataService.isImageDataset(dataset)
        ? (panel?.image ?? null)
        : null
      const video = dataset && !dataService.isImageDataset(dataset)
        ? (panel?.hlsService?.getVideo() ?? null)
        : null
      const cached = specCache[slot]
      if (
        cached
        && cached.dataset === dataset
        && cached.image === image
        && cached.video === video
      ) {
        return cached.spec
      }
      // Phase 3h: forward the same overlay options the 2D renderer
      // consumes (bbox / lonOrigin / flipY / celestialBody) so the
      // VR Phong material can clip regional datasets to their bbox
      // and reveal an Earth base diffuse outside. `undefined` for
      // datasets at all defaults — keeps legacy globals on the fast
      // path through the shader.
      let spec: VrDatasetTexture | null = null
      if (dataset) {
        const options = overlayOptionsFromDataset(dataset)
        if (image) spec = { kind: 'image', element: image, options }
        else if (video) spec = { kind: 'video', element: video, options }
      }
      specCache[slot] = { dataset, image, video, spec }
      return spec
    }

    await initVrButton({
      // Primary-focused convenience getters — equivalent to calling
      // the panel-slot version with the current primary index.
      getDatasetTexture: () => getPanelTexture(this.viewports.getPrimaryIndex()),
      getDatasetTitle: () => this.appState.currentDataset?.title ?? null,
      getDatasetId: () => this.appState.currentDataset?.id ?? null,
      getDatasetTimeLabel: () => {
        // Compute the label fresh from video.currentTime every call.
        //
        // Why not read appState.timeLabel? WebXR pauses
        // window.requestAnimationFrame while an immersive session
        // is live, which freezes `startPlaybackLoop` — the
        // per-frame loop that normally keeps appState.timeLabel
        // updated. A VR session reading appState.timeLabel would
        // see the value from the instant the user tapped Enter VR
        // and stay there forever. Recomputing here makes the
        // label advance on the XR animation loop instead, and as
        // a bonus gives us correct pause behaviour for free: a
        // paused video's currentTime doesn't advance, so the
        // formatted string stays fixed until playback resumes.
        const ds = this.appState.currentDataset
        if (!ds?.startTime) return null

        if (dataService.isVideoDataset(ds)) {
          const video = this.hlsService?.video
          if (!video || !ds.endTime) return null
          // `HTMLVideoElement.duration` is `NaN` until the video's
          // metadata has loaded — `?? 1` doesn't catch NaN because
          // it isn't nullish, so the previous check would pass NaN
          // straight into `videoTimeToDate` and `formatDate` would
          // render "Invalid Date". Suppress the label entirely
          // until duration is available; the per-frame poll will
          // pick it up the first frame after `loadedmetadata`.
          const duration = this.hlsService?.duration
          if (!Number.isFinite(duration) || (duration as number) <= 0) return null
          const start = new Date(ds.startTime)
          const end = new Date(ds.endTime)
          const snapMs = this.playback.displayInterval?.intervalMs
          const currentDate = videoTimeToDate(video.currentTime, duration as number, start, end, snapMs)
          const showTime = ds.period
            ? isSubDailyPeriod(ds.period)
            : (this.playback.displayInterval?.showTime ?? false)
          return formatDate(currentDate, showTime)
        }

        // Image dataset — the startTime is the only thing to show.
        const showTime = ds.period ? isSubDailyPeriod(ds.period) : false
        return formatDate(new Date(ds.startTime), showTime)
      },
      hasVideoDataset: () => {
        const ds = this.appState.currentDataset
        return !!ds && dataService.isVideoDataset(ds)
      },
      isPlaying: () => this.appState.isPlaying,
      togglePlayPause: () => togglePlayPause(
        this.hlsService, this.appState, (m) => this.announce(m),
      ),
      isMuted: () => {
        // Read directly off the primary's <video> element —
        // hlsService sets it muted by default for autoplay
        // compliance; the 2D mute button (and the new VR HUD mute
        // button) flip it when the user wants sound.
        return this.hlsService?.video?.muted ?? true
      },
      toggleMute: () => {
        const video = this.hlsService?.video
        if (!video) return
        video.muted = !video.muted
        this.announce(video.muted ? 'Muted' : 'Unmuted')
      },

      // --- Phase 2.5 multi-panel getters ---
      getPanelCount: () => this.viewports.getPanelCount(),
      getPrimaryIndex: () => this.viewports.getPrimaryIndex(),
      getPanelTexture,
      getPanelTitle: (slot: number) =>
        this.panelStates[slot]?.dataset?.title ?? null,

      // --- Phase 3 in-VR browse ---
      getDatasets: () => {
        return this.appState.datasets
          .filter(d => dataService.isSupportedDataset(d) && !d.isHidden)
          .map(d => {
            // Mirror the 2D browse UI (browseUI.ts line 79-90):
            // chips are the UNION of enriched.categories keys and
            // `Dataset.tags`, minus 'Movies' and 'Layers' (those
            // are internal shape-of-data markers, not user-facing
            // filters). Without the tags merge, chips like "Tours"
            // and "Real-Time" — which live exclusively on tags —
            // never appear.
            const cats = new Set<string>()
            if (d.enriched?.categories) {
              for (const k of Object.keys(d.enriched.categories)) cats.add(k)
            }
            if (d.tags) {
              for (const t of d.tags) cats.add(t)
            }
            cats.delete('Movies')
            cats.delete('Layers')
            return {
              id: d.id,
              title: d.title,
              categories: Array.from(cats),
              thumbnailUrl: d.thumbnailLink ?? null,
            }
          })
      },
      loadDataset: (id: string) => {
        void this.loadDataset(id, 'orbit')
      },

      // --- Phase 3.5 in-VR tour controls ---
      // `tourEngine` is null when no tour is running — the strip
      // keys off `active: false` in that case and stays hidden.
      // When a tour is running, state maps 1:1 onto TourEngine's
      // accessors. Tour actions fan out to the same methods the 2D
      // `showTourControls` bar wires up.
      getTourState: () => {
        const engine = this.tourEngine
        if (!engine) {
          return { active: false, isPlaying: false, step: 0, totalSteps: 0 }
        }
        return {
          active: engine.state !== 'stopped',
          isPlaying: engine.state === 'playing',
          step: engine.currentIndex,
          totalSteps: engine.totalSteps,
        }
      },
      tourTogglePlayPause: () => {
        const engine = this.tourEngine
        if (!engine) return
        if (engine.state === 'playing') engine.pause()
        else void engine.play()
      },
      tourPrev: () => {
        const engine = this.tourEngine
        if (!engine) return
        engine.prev()
        // Matches the 2D next-button behavior (tourUI.onNext): after
        // navigating, resume playback if the engine paused itself on
        // a pauseForInput waiting for user input.
        if (engine.state === 'paused') void engine.play()
      },
      tourNext: () => {
        const engine = this.tourEngine
        if (!engine) return
        engine.next()
        if (engine.state === 'paused') void engine.play()
      },
      tourStop: () => {
        // Routes through stopTour() not engine.stop() so the
        // standalone-tour "return home" behaviour still runs.
        this.stopTour()
      },

      onSessionEnd: () => {
        this.announce('Exited VR')
        // Resume the 2D perf sampler now that VR has handed the
        // GPU back. The sampler stayed paused for the duration of
        // the immersive session — VR has its own end-of-session
        // FPS metric on `vr_session_ended.mean_fps`.
        if (TELEMETRY_BUILD_ENABLED) resumeForVrExit()
      },
    })
  }

  /**
   * Wire playback positioning observers and, when this node offers
   * Orbit, initialize the chat panel. With Orbit off the chat is never
   * initialized — not merely hidden — because initialization restores
   * the session and can start hands-free voice, which opens the
   * microphone; `setOrbitAvailable(false)` hides the static chrome and
   * refuses every programmatic open instead.
   */
  private initChat(orbitEnabled: boolean): void {
    initPlaybackPositioning()
    if (!orbitEnabled) {
      setOrbitAvailable(false)
      return
    }
    // The node can turn Orbit off after this page booted; a kiosk tab
    // never reloads to find out. Re-read periodically and withdraw.
    watchForOrbitWithdrawal()
    initChatUI({
      onLoadDataset: (id) => { void this.selectDatasetFromChat(id) },
      onLoadFrame: (id, frameQuery) => { void this.loadFrameFromChat(id, frameQuery) },
      onFlyTo: (lat, lon, altitude) => {
        void this.renderer?.flyTo(lat, lon, altitude)
        // Also rotate the VR globe if a session is live. Fire-and-
        // forget — chat isn't awaiting the settle, and the VR-side
        // animation runs off the existing render loop regardless
        // of whether anyone holds the promise.
        if (isVrActive()) void flyToOnGlobe(lat, lon)
      },
      onSetTime: (isoDate) => seekToDate(isoDate, this.hlsService, this.appState, this.playback),
      canSetTime: (isoDate) => checkSeekToDate(isoDate, this.hlsService, this.appState),
      onFitBounds: (bounds, _label) => { this.renderer?.fitBounds(bounds) },
      onAddMarker: (lat, lng, label) => { this.renderer?.addMarker(lat, lng, label) },
      onToggleLabels: (visible) => {
        for (const r of this.viewports.getAll()) { r.toggleLabels?.(visible); r.toggleBoundaries?.(visible) }
      },
      onHighlightRegion: (geojson, _label) => { this.renderer?.highlightRegion(geojson) },
      onShowAnalysis: (scope, regionName) => {
        openAnalyzeUI(
          null,
          scope === 'named' && regionName
            ? { kind: 'named', name: regionName }
            : scope === 'view'
              ? { kind: 'view' }
              : { kind: 'dataset' },
        )
      },
      getMapViewContext: () => this.renderer?.getViewContext() ?? null,
      getDatasets: () => this.appState.datasets,
      getCurrentDataset: () => this.appState.currentDataset,
      announce: (msg) => this.announce(msg),
      onOpenBrowse: () => this.openBrowsePanel(),
      onVoiceAudioFocus: (active) => this.duckDatasetAudio(active),
    })
  }

  /**
   * Phase 3 hands-free — duck the loaded dataset's audio during a voice
   * turn so the mic doesn't transcribe it and TTS doesn't compete (§9.1).
   * The HLS `<video>` is muted by default; this only has an audible
   * effect once the user has unmuted via the transport mute button. We
   * lower the volume rather than toggling `.muted`, so the user's mute
   * choice is preserved, and restore the prior volume afterward.
   */
  private duckPrevVolume: number | undefined = undefined

  private duckDatasetAudio(active: boolean): void {
    const video = this.hlsService?.video
    if (!video) return
    if (active) {
      if (this.duckPrevVolume === undefined) this.duckPrevVolume = video.volume
      video.volume = Math.min(video.volume, 0.12)
    } else if (this.duckPrevVolume !== undefined) {
      video.volume = this.duckPrevVolume
      this.duckPrevVolume = undefined
    }
  }

  /**
   * Phase 3pt/A — mount the tour-authoring dock when the URL
   * carries `?tourEdit=<id>` (or `=new` for a fresh draft).
   * No-op on regular page loads. The dock reads from the primary
   * renderer's view context to compose camera-step tasks; its
   * "Discard" button strips the URL param and routes back to
   * the publisher tour list.
   */
  private initTourAuthoring(): void {
    initTourAuthoring({
      getMapView: () => this.renderer?.getViewContext() ?? null,
      getCurrentDataset: () => this.appState.currentDataset,
      onDiscard: () => {
        // Clear the query string and go to the publisher portal's
        // tour list. Using `location.assign` so the back-button
        // history stays clean (a tour-edit URL bouncing in the
        // history is more confusing than the simple linear path).
        window.location.assign('/publish/tours')
      },
      onPreview: (tourFile) => {
        this.playInlineTour(tourFile)
      },
    })
  }

  /**
   * Phase 3pt-review/G — preview the in-memory tour draft.
   * Pre-fix (review/G + /I) this wrapped the TourFile in a
   * blob URL and routed through `playTour`, but Firefox + a
   * strict `connect-src` CSP refuses to let the page `fetch()`
   * a `blob:` of itself ("Content at ... may not load data
   * from blob:..."). The fetch was pure overhead anyway — the
   * TourFile is already in memory. Now hands the file straight
   * to `runTourFromFile`, which is the post-fetch half of the
   * old `startTour`. Standalone (`playTour`) still fetches
   * because it gets a real URL and needs `tourBaseUrl` for
   * relative-media-path resolution. Phase 3pt-review/J —
   * follow-up after the published bundle hit the CSP bug in
   * the wild.
   *
   * Preview is deliberately NOT standalone: `endTour` calls
   * `goHome()` for standalone tours, which would strip
   * `?tourEdit=` from the URL and unmount the dock when a
   * preview finishes naturally. Bumping `loadGeneration`
   * matches the supersede-in-flight-loads behaviour from the
   * old `playTour` (the previous call ran
   * `++this.loadGeneration` before `startTour`; we replicate
   * it here so a dataset load in flight when Preview fires
   * can't land mid-tour and clobber state). Copilot
   * discussion_r3293605064.
   */
  playInlineTour(tourFile: TourFile): void {
    this.stopTour()
    // Defensive — `stopTour` only clears `tourIsStandalone` when
    // `this.tourEngine` is non-null. If a `playTour` is mid-fetch
    // (engine not yet created), the flag from that call would
    // leak into the preview and trip the goHome branch in
    // `endTour`. The loadGeneration bump below supersedes the
    // in-flight fetch, but we still need to clear the flag the
    // bumped fetch already set. Copilot discussion_r3293611214.
    this.tourIsStandalone = false
    ++this.loadGeneration
    this.runTourFromFile(tourFile, window.location.href)
  }

  /**
   * The browse panel's "ask Orbit" hook, or `undefined` when this node
   * has Orbit off — browse then offers no docent hint under an empty
   * search rather than a button that does nothing. Browse keeps the
   * callbacks of its *first* `showBrowseUI` call, and every boot path
   * makes that call after `initChat` decided. A page that loses Orbit
   * mid-session keeps the captured hook; `body.orbit-disabled` hides
   * the hint it renders, and `openChatWithQuery` refuses the click.
   */
  private browseOpenChat(): ((query?: string) => void) | undefined {
    return isOrbitAvailable() ? (query) => this.openChatWithQuery(query) : undefined
  }

  /**
   * Fetch the dataset's legend and have the vision model describe it
   * for Orbit's context. This runs on every load, chat open or not, so
   * it is skipped outright when the node has Orbit off — the
   * description has no other reader, and the call would only meet the
   * chat endpoint's `feature_disabled` refusal.
   */
  private primeOrbitLegend(dataset: Dataset): void {
    if (!isOrbitAvailable()) return
    initLegendForDataset(dataset, loadConfig())
  }

  /** Open the chat panel and optionally pre-fill the input with a query string. */
  private openChatWithQuery(query?: string): void {
    if (!isOrbitAvailable()) return
    openChat()
    if (query) {
      const input = document.getElementById('chat-input') as HTMLTextAreaElement | null
      if (input) {
        input.value = query
        input.style.height = 'auto'
        input.style.height = Math.min(input.scrollHeight, 96) + 'px'
      }
    }
  }


  /** Load a dataset selected via the chat panel, updating URL and notifying chat of the change. */
  /** Orbit-driven dataset load. Tags the layer_loaded trigger so we
   * can tell chat-initiated loads apart from browse/url/tour. */
  /**
   * Phase 3pg/C — resolve a frame query against the parent
   * sequence dataset and load the resulting image. Falls back to
   * loading the parent dataset (HLS) if the row has no frames
   * envelope, so the user always gets something useful even if
   * the LLM emitted a load_frame against a non-sequence row.
   *
   * The frame is rendered as a one-off image overlay on the
   * globe — same code path the existing image-dataset loader
   * uses. The parent sequence is not modified; clicking a
   * different frame later re-renders against the same dataset
   * row.
   */
  private async loadFrameFromChat(datasetId: string, frameQuery: string): Promise<void> {
    const dataset = this.appState.datasets.find(d => d.id === datasetId)
    if (!dataset) {
      logger.warn('[App] loadFrameFromChat: unknown dataset', datasetId)
      return
    }
    if (!dataset.frames) {
      // Non-sequence row — chat UI shouldn't have emitted a
      // load-frame for this, but defend against a stale catalog /
      // race between manifest refresh and click.
      logger.warn('[App] loadFrameFromChat: dataset has no frames envelope; falling back to dataset load.')
      void this.selectDatasetFromChat(datasetId)
      return
    }
    const resolved = resolveFrameQuery(dataset, frameQuery)
    if (!resolved) {
      logger.warn('[App] loadFrameFromChat: could not resolve frame query', { datasetId, frameQuery })
      return
    }
    this.announce(`Loading frame: ${resolved.displayName}`)
    // Build a synthetic single-frame dataset off the parent's
    // metadata. Format is `image/*` so the loader picks the image
    // render path (rather than HLS). Title carries the display
    // name so the info panel reads cleanly.
    //
    // Derive the format from the resolved URL's extension rather
    // than hard-coding `image/png` — the server allows `jpg` /
    // `webp` sequences too and bakes the extension into
    // `frames.urlTemplate`. A hard-coded PNG would mislead any
    // downstream logic that branches on `dataset.format`. Phase
    // 3pg-review/C — Copilot discussion_r3277396472.
    const format = mimeForFrameUrl(resolved.url)
    const frameDataset: Dataset = {
      ...dataset,
      id: `${dataset.id}#frame=${resolved.index}`,
      title: `${dataset.title} — ${resolved.displayName}`,
      format: format as Dataset['format'],
      dataLink: resolved.url,
      // Drop the parent's time controls; a single frame is a
      // still image, not a playback target.
      startTime: undefined,
      endTime: undefined,
      period: undefined,
      frames: undefined,
    }
    const primary = this.viewports.getPrimary()
    if (!primary) {
      logger.warn('[App] loadFrameFromChat: no primary renderer; cannot render frame')
      return
    }
    // Mirror the normal `loadDataset` teardown order:
    //   1. Stop playback + drop any active HLS / video texture on
    //      the primary slot — otherwise a frame-load while a video
    //      is playing leaves the playback loop running and the
    //      HLS service downloading segments behind a hidden video
    //      element. The time label would also keep ticking against
    //      a dataset that's no longer displayed. Phase 3pg-review/D
    //      — Copilot discussion_r3277695258.
    //   2. Hide the visual overlays the regular dataset-load path
    //      hides (cloud / day-night / sun). Phase 3pg-fix —
    //      pass-1 follow-up.
    //   3. Explicitly hide the playback transport + time label.
    //      The `loaderCallbacks` below run AFTER `loadImageDataset`
    //      decides what to show, so the loader-callback path can't
    //      reliably tear down state already visible from a prior
    //      video load. Calling these directly before the load is
    //      what matches the normal flow's
    //      `if (!isImageDataset) showPlaybackControls(true)`
    //      logic — image datasets explicitly hide them, and frame
    //      loads are image datasets. Phase 3pg-review/D — Copilot
    //      discussion_r3277695282.
    stopPlaybackLoop(this.playback)
    this.appState.isPlaying = false
    resetPlaybackState(this.playback)
    this.cleanupPanelVideo()
    primary.removeCloudOverlay?.()
    primary.removeNightLights?.()
    primary.disableSunLighting?.()
    this.showPlaybackControls(false)
    this.showTimeLabel(false)
    // The loader callbacks stay as no-ops because the show/hide
    // decisions are now made above; if a future loader change
    // starts calling them mid-load they'd be the wrong side of
    // the teardown. Match the rest of the no-op image paths.
    const loaderCallbacks = {
      showPlaybackControls: (_show: boolean) => { /* handled above */ },
      showTimeLabel: (_show: boolean) => { /* handled above */ },
    }
    try {
      // `directImageUrl: true` skips the loader's legacy `_4096` /
      // `_2048` / `_1024` resolution-suffix probing — those would
      // 404 against a per-frame R2 URL, adding three failed round-
      // trips before the actual URL resolves. Phase 3pg/C — Copilot
      // discussion_r3277041026.
      await loadImageDataset(
        frameDataset,
        primary,
        this.appState,
        this.isMobile,
        loaderCallbacks,
        { directImageUrl: true },
      )
      this.appState.currentDataset = frameDataset
      notifyDatasetChanged(frameDataset)
      this.announce(`Loaded frame: ${resolved.displayName}`)
    } catch (err) {
      logger.error('[App] loadFrameFromChat failed:', err)
      this.announce('Failed to load frame.')
    }
  }

  private async selectDatasetFromChat(id: string): Promise<void> {
    const gen = ++this.loadGeneration
    logger.debug('[App] selectDatasetFromChat:', id, 'gen:', gen)
    this.dismissBrowseAfterLoad()
    this.announce('Loading dataset\u2026')
    this.showLoadingScreen('Loading dataset\u2026', 20)
    this.writeDatasetUrl(id, 'push')
    await this.loadDataset(id, 'browse')
    if (gen !== this.loadGeneration) {
      logger.debug('[App] selectDatasetFromChat superseded:', id, 'gen:', gen, 'current:', this.loadGeneration)
      return
    }
    this.setLoading(false)
    showChatTrigger()
    const dataset = this.appState.currentDataset
    if (dataset) {
      this.announce(`Loaded dataset: ${dataset.title}`)
      this.renderer?.setCanvasDescription(`3D globe showing ${dataset.title}`)
      notifyDatasetChanged(dataset)
      setHelpActiveDataset(dataset.id)
      // Flush deferred globe-control actions (fly-to, set-time) now that the dataset is loaded
      flushPendingGlobeActions()
    }
  }

  /**
   * Called by ViewportManager when a setLayout adds/removes panels.
   * Resizes our parallel panelStates array to match, disposing any
   * videos in slots that are going away, and seeds newly-created
   * panels with the current map-controls toolbar state (labels,
   * borders, terrain) so they start in sync with the primary.
   */
  private onViewportLayoutChange(newCount: number, oldCount: number): void {
    if (newCount < oldCount) {
      // Tear down slots that are being removed
      for (let i = newCount; i < oldCount; i++) {
        const panel = this.panelStates[i]
        if (!panel) continue
        this.cancelSiblingSeekUpload(i)
        if (panel.videoTexture) { panel.videoTexture.dispose() }
        if (panel.hlsService) { panel.hlsService.destroy() }
      }
      this.panelStates.length = newCount
    } else {
      // Add fresh empty slots for new panels
      while (this.panelStates.length < newCount) {
        this.panelStates.push(createPanelState())
      }
      // Seed the new viewports with the current toolbar state so
      // they visually match the primary's labels/borders/terrain.
      // We read state from the DOM button classes rather than
      // querying a renderer, since the new renderers may still be
      // mid-style-load when this fires. MapLibre queues layer
      // operations internally until the style is ready, so
      // dispatching the toggle now is safe.
      const labelsActive = document.getElementById('tools-menu-labels')?.classList.contains('active') ?? false
      const bordersActive = document.getElementById('tools-menu-borders')?.classList.contains('active') ?? false
      const terrainActive = document.getElementById('tools-menu-terrain')?.classList.contains('active') ?? false
      for (let i = oldCount; i < newCount; i++) {
        const newRenderer = this.viewports.getRendererAt(i)
        if (!newRenderer) continue
        const map = newRenderer.getMap()
        const applyOverlayState = () => {
          newRenderer.toggleLabels?.(labelsActive)
          newRenderer.toggleBoundaries?.(bordersActive)
          if (terrainActive) newRenderer.toggleTerrain?.(true)
        }
        if (map && map.isStyleLoaded()) {
          applyOverlayState()
        } else {
          map?.once('load', applyOverlayState)
        }
      }
    }
    // Layout change can affect whether a panel's legend is a
    // floating element vs inside the info panel, and the set of
    // available datasets in the info-panel picker — refresh both.
    // Also clear any picker override if the pinned slot just
    // vanished on a shrink.
    if (
      this.infoDisplayOverride !== null &&
      this.infoDisplayOverride >= newCount
    ) {
      this.infoDisplayOverride = null
    }
    this.refreshPanelLegends()
    this.renderInfoPanel()
    syncToolsMenuLayout(this.viewports.getLayout())
  }

  /**
   * Called by ViewportManager when the primary index changes (user
   * clicked a non-primary panel's indicator badge). Rewires the
   * singular UI — info panel, playback controls, video sync, URL —
   * to the new primary's dataset.
   */
  private onViewportPrimaryChange(newIndex: number, _oldIndex: number): void {
    logger.debug('[App] Primary panel changed:', _oldIndex, '→', newIndex)
    const newPrimaryPanel = this.panelStates[newIndex]
    const newDataset = newPrimaryPanel?.dataset ?? null

    // Analyze reads the primary and nothing else, and its transect is
    // drawn on that panel's map. Promoting a different one would leave
    // the line on the globe the panel is no longer describing, so it
    // closes here rather than being rewired — the same reasoning as the
    // dataset-swap teardown, which `notifyAnalyzeDatasetChanged` below
    // would miss whenever both panels hold the same row.
    closeAnalyzeUI()

    // Rewire video sync to the new primary's video (if any)
    this.detachPrimaryVideoSync()
    stopPlaybackLoop(this.playback)
    // And the outputs' camera: a listener left on the demoted panel's
    // map would keep driving them from a globe the operator is no
    // longer using.
    this.bindOperatorCamera()

    // Update the shared appState + info panel. Promoting a different
    // panel clears any picker override so the info panel follows the
    // new primary instead of silently pointing at a stale slot.
    this.appState.currentDataset = newDataset
    this.infoDisplayOverride = null
    if (newDataset) {
      this.renderInfoPanel()
      this.writeDatasetUrl(newDataset.id, 'replace')
      notifyDatasetChanged(newDataset)
      setHelpActiveDataset(newDataset.id)
      this.renderer?.setCanvasDescription(`3D globe showing ${newDataset.title}`)
    } else {
      const infoPanel = document.getElementById('info-panel')
      if (infoPanel) infoPanel.classList.add('hidden')
      this.clearDatasetUrl('replace')
      notifyDatasetChanged(null)
      setHelpActiveDataset(null)
      this.renderer?.setCanvasDescription('Interactive 3D globe showing Earth')
    }
    // Legend placement depends on primary (single-view uses the
    // info panel's thumbnail for the primary; multi-view always
    // uses per-panel floating legends). Refresh so the primary's
    // legend moves to the correct surface.
    this.refreshPanelLegends()

    // Rewire playback controls based on the new primary's video state
    const isVideo = newDataset && dataService.isVideoDataset(newDataset) && newPrimaryPanel?.hlsService
    const hasTemporalRange = Boolean(newDataset?.startTime && newDataset?.endTime)
    if (isVideo) {
      this.showPlaybackControls(true)
      this.showTimeLabel(hasTemporalRange)
      updatePlayButton(newPrimaryPanel!.hlsService!.paused)
      // Recompute the display interval for the new primary's temporal
      // range so scrubber snapping and time label formatting are correct.
      // Pass `period` and `frames.count` for an imagery-accurate cadence
      // (Plan §5.1) — without these, multi-year climate datasets fall
      // back to the snap-list estimate and tick the label too fast.
      if (hasTemporalRange && newPrimaryPanel!.hlsService) {
        const start = new Date(newDataset!.startTime!)
        const end = new Date(newDataset!.endTime!)
        const videoDuration = newPrimaryPanel!.hlsService!.duration ?? 1
        this.playback.displayInterval = inferDisplayInterval(start, end, videoDuration, {
          period: newDataset!.period,
          frameCount: newDataset!.frames?.count,
        })
      }
      this.attachPrimaryVideoSync()
      this.doStartPlaybackLoop()
    } else {
      this.showPlaybackControls(false)
      if (!hasTemporalRange) {
        this.showTimeLabel(false)
      }
    }
    this.announce(newDataset ? `Active panel: ${newDataset.title}` : `Panel ${newIndex + 1} active`)

    // Promotion changes which dataset the primary is showing without
    // any load or unload, so an output driven only by those two would
    // keep showing the demoted panel's row indefinitely.
    this.publishMirroredDataset()
  }

  /**
   * Store a video-load result into a specific panel's state.
   * Assumes the slot exists (callers must validate the index).
   */
  private storePanelVideoResult(
    slot: number,
    result: { hlsService: HLSService; videoTexture: VideoTextureHandle },
    datasetId: string,
  ): void {
    const panel = this.panelStates[slot]
    if (!panel) return
    panel.hlsService = result.hlsService
    panel.videoTexture = result.videoTexture
    // Recorded with the stream, not with the catalog row: this is the
    // claim "the pixels on this panel are this dataset's".
    panel.mediaDatasetId = datasetId

    // A stream that dies after load used to say nothing at all, which
    // left this panel holding a frame for good while the shared time
    // label went on asserting a moment for it. Clear any stale mark
    // first: this slot may be being reused by a new dataset.
    this.viewports.setPanelStreamNotice(slot, false)
    result.hlsService.onFatalError((error) => {
      // Guard against a late callback for a dataset this slot no
      // longer holds — the tour swaps sibling datasets repeatedly.
      if (this.panelStates[slot]?.hlsService !== result.hlsService) return
      logger.error(`[App] Panel ${slot} stream failed terminally: ${error.type}/${error.details}`)
      this.viewports.setPanelStreamNotice(slot, true)
    })
  }

  /**
   * Sibling video sync — primary-as-master-clock, per-frame correction.
   *
   * The primary panel's video is the single source of truth for the
   * current real-world date; siblings are kept locked to it. Rather
   * than set a once-off `playbackRate` and let siblings free-run (which
   * integrates `video.duration` imprecision into visible drift — see
   * terraviz#132), we re-derive each sibling's target position from the
   * primary's date every frame and steer it back into alignment.
   *
   *   - **On play / seeked** (`seekSiblingsToDate`): exact-align every
   *     sibling to the primary's date and mirror its play/pause state.
   *     Runs once on attach and on the primary's `play` / `seeked`.
   *
   *   - **On pause** (`seekSiblingsToDate`): exact-align overlapping
   *     siblings to the primary's date *then* freeze them, so the held
   *     frame is frame-accurate for side-by-side study. Out-of-range
   *     siblings stay frozen at their nearest boundary frame.
   *
   *   - **Per-frame drift correction** (`correctSiblingDrift`, driven
   *     from the playback rAF loop while the primary is playing): every
   *     frame, re-derive each sibling's target/rate. Small drift is
   *     eased out by gently trimming `playbackRate` (no seek — a
   *     `currentTime` write on a playing video interrupts decode and
   *     flickers the panel; terraviz#229). A hard seek fires only for a
   *     large desync past {@link SIBLING_HARD_SEEK_THRESHOLD_S}, or to
   *     pin an out-of-range sibling to its boundary frame.
   *
   * Net: drift is bounded by the threshold (one frame to correct) rather
   * than by an interval, and seeking stays near-zero in steady state
   * because the smoothing rate keeps siblings inside the threshold.
   */
  private attachPrimaryVideoSync(): void {
    this.detachPrimaryVideoSync()
    const primaryIdx = this.viewports.getPrimaryIndex()
    const primaryPanel = this.panelStates[primaryIdx]
    const primaryHls = primaryPanel?.hlsService
    const primaryVideo = primaryHls?.getVideo?.() ?? null
    if (!primaryVideo) return

    /**
     * Seek every sibling to the primary's current date-mapped
     * position, update out-of-range indicators, and optionally
     * mirror the primary's play/pause state.
     *
     * @param mirrorPlayState If true, also play/pause siblings
     * to match the primary. On periodic drift checks we skip this
     * (they're already playing/paused) to avoid re-triggering
     * play() on videos that are fine.
     */
    const seekSiblingsToDate = (mirrorPlayState: boolean) => {
      const pIdx = this.viewports.getPrimaryIndex()
      const pPanel = this.panelStates[pIdx]
      const pDataset = pPanel?.dataset

      let primaryDate: Date | null = null
      if (pDataset?.startTime && pDataset.endTime && primaryVideo.duration > 0) {
        primaryDate = videoTimeToDate(
          primaryVideo.currentTime,
          primaryVideo.duration,
          new Date(pDataset.startTime),
          new Date(pDataset.endTime),
        )
      }

      for (let i = 0; i < this.panelStates.length; i++) {
        if (i === pIdx) continue
        const sibPanel = this.panelStates[i]
        const sibHls = sibPanel?.hlsService
        const sibVideo = sibHls?.getVideo?.() ?? null
        // Metadata is enough to steer a sibling — see
        // {@link SIBLING_MIN_READY_STATE}. This is the call that has to
        // reach a sibling parked at its own end when the primary wraps,
        // so it must not wait for frame data the seek itself will fetch.
        if (!sibVideo || sibVideo.readyState < SIBLING_MIN_READY_STATE) continue

        const sibDataset = sibPanel?.dataset
        const sibHasRange = !!(sibDataset?.startTime && sibDataset.endTime && sibVideo.duration > 0)

        if (primaryDate && sibHasRange) {
          const { videoTime: targetTime, position } = dateToVideoTime(
            primaryDate,
            sibVideo.duration,
            new Date(sibDataset!.startTime!),
            new Date(sibDataset!.endTime!),
          )

          // Match the sibling's playback speed so it advances through
          // real-world time at the same pace as the primary, even if
          // the two videos have different durations (e.g. daily vs
          // weekly frames). Without this, a shorter sibling finishes
          // its entire date range while the primary is still early in
          // its timeline, causing constant drift corrections.
          const primaryRangeMs = new Date(pDataset!.endTime!).getTime() - new Date(pDataset!.startTime!).getTime()
          const sibRangeMs = new Date(sibDataset!.endTime!).getTime() - new Date(sibDataset!.startTime!).getTime()
          if (primaryRangeMs > 0 && sibRangeMs > 0) {
            // rate = (sib video seconds per real-world ms) / (primary video seconds per real-world ms)
            // Simplifies to: (sibDuration / sibRangeMs) / (primaryDuration / primaryRangeMs)
            // Scaled by the primary's actual playbackRate so the sibling
            // tracks tour playback-rate changes (e.g. 5fps → 0.167×),
            // not just the 1× case (terraviz#229).
            const rate = (sibVideo.duration / sibRangeMs) / (primaryVideo.duration / primaryRangeMs) * primaryVideo.playbackRate
            // Clamp to browser limits (typically 0.0625–16×)
            sibVideo.playbackRate = Math.max(0.0625, Math.min(16, rate))
          }

          if (position === 'inside') {
            this.viewports.setOutOfRange(i, false)
            this.alignSiblingTo(i, sibVideo, targetTime)
            if (mirrorPlayState) {
              if (primaryVideo.paused && !sibVideo.paused) {
                sibVideo.pause()
              } else if (!primaryVideo.paused && sibVideo.paused) {
                sibVideo.play().catch(() => { /* autoplay blocked */ })
              }
            }
          } else {
            this.viewports.setOutOfRange(i, true)
            this.alignSiblingTo(i, sibVideo, targetTime)
            if (!sibVideo.paused) sibVideo.pause()
          }
        } else {
          this.viewports.setOutOfRange(i, false)
          if (mirrorPlayState) {
            if (primaryVideo.paused && !sibVideo.paused) {
              sibVideo.pause()
            } else if (!primaryVideo.paused && sibVideo.paused) {
              sibVideo.play().catch(() => { /* autoplay blocked */ })
            }
          }
        }

        this.uploadSiblingFrameOnSeek(i, sibVideo)
      }
    }

    // --- Wire event listeners ---

    const onPlay = () => seekSiblingsToDate(true)
    // On pause, exact-align every overlapping sibling to the primary's
    // date *before* freezing, so the held frame is frame-accurate for
    // side-by-side study — paused is exactly when a viewer scrutinises
    // the comparison, and a one-shot seek can't thrash. seekSiblingsToDate
    // both snaps in-range siblings to the exact date and (because the
    // primary is already paused here) mirrors the pause to them; siblings
    // whose window doesn't contain the date stay frozen at their boundary.
    const onPause = () => seekSiblingsToDate(true)
    const onSeeked = () => seekSiblingsToDate(true)

    primaryVideo.addEventListener('play', onPlay)
    primaryVideo.addEventListener('pause', onPause)
    primaryVideo.addEventListener('seeked', onSeeked)
    this.primaryVideoSyncListeners.push(
      { event: 'play', handler: onPlay as EventListener },
      { event: 'pause', handler: onPause as EventListener },
      { event: 'seeked', handler: onSeeked as EventListener },
    )
    this.primaryVideoSyncTarget = primaryVideo

    // Arm per-frame drift correction (driven from the playback rAF
    // loop via `correctSiblingDrift`) and run a full resync once now so
    // siblings reflect the primary's current state immediately.
    this.primaryVideoSyncActive = true
    seekSiblingsToDate(true)
  }

  /**
   * Per-frame sibling drift correction, invoked from the playback rAF
   * loop (`startPlaybackLoop`'s `onTick`). The primary video is the
   * master clock: every frame we re-derive each sibling's target
   * position from the primary's real-world date and snap it back if it
   * has drifted past {@link SIBLING_DRIFT_THRESHOLD_S}.
   *
   * This replaces the old once-set-`playbackRate` + 5 s/1 s interval
   * scheme (terraviz#132). The rate is kept only as a smoothing aid so
   * we don't seek every frame — it's re-derived continuously here so it
   * tracks `video.duration` as HLS segments buffer and the reported
   * duration firms up (the original failure mode: an imprecise duration
   * froze a slightly-wrong rate whose error integrated into visible
   * drift — ~2.83 years per second of video on the Climate Futures
   * tour). The threshold-gated seek is the closed loop that bounds it.
   */
  /**
   * Detach the camera listener from whichever map it was on.
   *
   * Held as a field rather than re-derived, because the primary map is
   * replaced on promotion and a listener left on the old one keeps
   * publishing a camera nobody is driving — the outputs would follow a
   * panel the operator demoted.
   */
  private unbindOperatorCamera: (() => void) | null = null

  /**
   * Publish the primary's camera to any outputs (§3, rung 7).
   *
   * MapLibre's `move` fires once per rendered frame during a drag,
   * which is the rate step 15's "≤30 ms lag" asks for and the reason
   * this is not additionally throttled. It costs nothing when the globe
   * is still: `publishGlobeState` hands the patch to the aggregator,
   * which drops a value structurally identical to the one it holds, so
   * a parked camera puts nothing on the wire.
   *
   * Bound here rather than in `initialize` so promotion rebinds it —
   * `onViewportPrimaryChange` calls this again with the new primary.
   */
  private bindOperatorCamera(): void {
    this.unbindOperatorCamera?.()
    this.unbindOperatorCamera = null

    const map = this.viewports.getPrimary()?.getMap()
    if (!map) return

    const publish = () => {
      const center = map.getCenter()
      publishGlobeState({
        view: sharedViewFrom(
          operatorCameraFrom(center.lat, center.lng, map.getZoom(), map.getBearing()),
        ),
      })
    }
    map.on('move', publish)
    this.unbindOperatorCamera = () => map.off('move', publish)
    // Once immediately: a promotion changes the camera without moving
    // it, and an output would otherwise keep the demoted panel's view
    // until the operator next touched the globe.
    publish()
  }

  /**
   * Publish the primary's playhead to any outputs (§3, rung 7).
   *
   * Called from the same `onTick` as `correctSiblingDrift`, but
   * deliberately *not* from inside it: that method returns early while
   * the primary is paused, and a paused primary is exactly the state an
   * output most needs told about — it has a position to hold, and
   * without this it would sit wherever its own element happened to
   * stop.
   *
   * `primary` is published alongside because `outputSync` gates on
   * both. It changes only on a dataset load, and the aggregator drops
   * the repeats, so sending it per frame costs one structural compare.
   */
  private publishPlaybackMirror(): void {
    const panel = this.panelStates[this.viewports.getPrimaryIndex()]
    const video = panel?.hlsService?.getVideo?.() ?? null
    const dataset = panel?.dataset

    if (!video || !dataset) {
      publishGlobeState({ playback: null, primary: null })
      return
    }

    publishGlobeState({
      playback: playbackFrom({
        currentTime: video.currentTime,
        duration: video.duration,
        paused: video.paused,
        playbackRate: video.playbackRate,
        startTime: dataset.startTime,
        endTime: dataset.endTime,
      }),
      primary: primaryFrom(video.duration, dataset.startTime, dataset.endTime),
    })
  }

  private correctSiblingDrift(): void {
    if (!this.primaryVideoSyncActive) return

    const pIdx = this.viewports.getPrimaryIndex()
    const pPanel = this.panelStates[pIdx]
    const pDataset = pPanel?.dataset
    const primaryVideo = pPanel?.hlsService?.getVideo?.() ?? null
    // Only correct while the primary is actively playing; paused state
    // is handled by the `pause` event handler in attachPrimaryVideoSync.
    if (!primaryVideo || primaryVideo.paused || primaryVideo.readyState < 2) return
    if (!pDataset?.startTime || !pDataset.endTime || primaryVideo.duration <= 0) return

    const primaryDate = videoTimeToDate(
      primaryVideo.currentTime,
      primaryVideo.duration,
      new Date(pDataset.startTime),
      new Date(pDataset.endTime),
    )

    const primaryRangeMs = new Date(pDataset.endTime).getTime() - new Date(pDataset.startTime).getTime()

    for (let i = 0; i < this.panelStates.length; i++) {
      if (i === pIdx) continue
      const sibPanel = this.panelStates[i]
      const sibVideo = sibPanel?.hlsService?.getVideo?.() ?? null
      // See {@link SIBLING_MIN_READY_STATE}. A sibling below
      // HAVE_CURRENT_DATA is exactly the one that needs correcting: the
      // desync at a loop wrap is the whole video duration, far past the
      // hard-seek threshold, and the corrective write is what restores it.
      if (!sibVideo || sibVideo.readyState < SIBLING_MIN_READY_STATE) continue

      const sibDataset = sibPanel?.dataset
      if (!sibDataset?.startTime || !sibDataset.endTime || sibVideo.duration <= 0) continue

      // Re-derive target/rate/drift every frame so the rate tracks
      // `video.duration` as HLS firms it up. Small in-range drift is
      // eased out via the returned (gently trimmed) rate; `shouldSeek`
      // is true only for a large desync or to pin an out-of-range
      // sibling to its boundary frame (terraviz#229 — per-frame seeking
      // interrupted decode and flickered the sibling).
      const { position, targetTime, rate, shouldSeek } = computeSiblingSyncCorrection({
        date: primaryDate,
        sibCurrentTime: sibVideo.currentTime,
        sibDuration: sibVideo.duration,
        sibStart: new Date(sibDataset.startTime),
        sibEnd: new Date(sibDataset.endTime),
        primaryDuration: primaryVideo.duration,
        primaryRangeMs,
        primaryPlaybackRate: primaryVideo.playbackRate,
        hardSeekThresholdS: SIBLING_HARD_SEEK_THRESHOLD_S,
      })

      sibVideo.playbackRate = rate

      // A seek already in flight is left alone. This runs every frame,
      // and a lower hard-seek threshold means it fires during the
      // ~2s a sibling spends re-buffering after a scrub — precisely
      // when it is least able to absorb another one. Writing
      // `currentTime` again there restarts the fetch hls.js is already
      // running, so a stall could be extended indefinitely by the very
      // correction meant to end it. The seek in flight is heading close
      // to the right place; whatever it misses by, the next frame after
      // it lands will still see and correct.
      if (shouldSeek && !sibVideo.seeking) {
        sibVideo.currentTime = targetTime
        this.uploadSiblingFrameOnSeek(i, sibVideo)
      }

      if (position === 'inside') {
        this.viewports.setOutOfRange(i, false)
        const wasOutOfRange = this.siblingOutOfRange[i] === true
        this.siblingOutOfRange[i] = false
        // Resume a sibling that was frozen out-of-range and has now
        // re-entered its window as the primary advanced. Only on the
        // transition — re-issuing play() every frame would churn
        // Promises (and log warnings) if autoplay is blocked.
        if (wasOutOfRange && sibVideo.paused) {
          sibVideo.play().catch(() => { /* autoplay blocked */ })
        }
      } else {
        // Primary date is outside this sibling's range — it's been
        // pinned to its boundary frame above; freeze + mark it.
        this.viewports.setOutOfRange(i, true)
        this.siblingOutOfRange[i] = true
        if (!sibVideo.paused) sibVideo.pause()
      }
    }
  }

  /**
   * Move a sibling to a target position — but only if it is not already
   * there.
   *
   * A seek is expensive in a way the old unconditional write assumed it
   * was not. Browser capture of a 4-globe session: four panels already
   * aligned at 0.5820 of their duration, pressing play seeked the three
   * siblings to 0.5822 — a fifth of one frame — and left all three at
   * `HAVE_METADATA` for five seconds, frozen on their previous frame
   * while the primary played on. Every play, pause and scrub was paying
   * that, because `seekSiblingsToDate` wrote `currentTime` whether or
   * not it changed anything.
   *
   * Within {@link SIBLING_SEEK_EPS_S} the panel is on the same frame it
   * would seek to, so the seek is skipped — but the texture is still
   * nudged, since being on the right frame and *showing* it are
   * different claims, and the second is the one that keeps failing.
   */
  private alignSiblingTo(slot: number, video: HTMLVideoElement, targetTime: number): void {
    if (Math.abs(video.currentTime - targetTime) <= SIBLING_SEEK_EPS_S) {
      const tex = this.panelStates[slot]?.videoTexture
      if (tex) tex.needsUpdate = true
      return
    }
    video.currentTime = targetTime
    this.uploadSiblingFrameOnSeek(slot, video)
  }

  /**
   * Upload a sibling's frame once its seek has actually landed.
   *
   * Setting `needsUpdate` straight after writing `currentTime` schedules
   * a repaint that arrives before the seek completes, so it uploads the
   * frame the panel was *already* showing and clears the one-shot flag.
   * The video is then paused, `!paused` is false, and the repaint
   * heartbeat in `earthTileLayer` does not run — nothing re-uploads,
   * ever. The panel keeps its pre-seek frame for good while its clock
   * reads the right time, which is how a globe ends up two hours behind
   * the label with no way back.
   *
   * Measured seeks complete in ~2 ms against locally buffered media,
   * which is why this never showed up in instrumented tests; against
   * real HLS a sibling can hold `HAVE_METADATA` for ~2 s, and the
   * repaint wins every time.
   *
   * `seeked` only fires once the data is there, so the upload it arms
   * cannot land early. The texture is looked up when the event fires
   * rather than captured, so a dataset swap in between retargets it
   * instead of writing through a stale handle.
   */
  private uploadSiblingFrameOnSeek(slot: number, video: HTMLVideoElement): void {
    const tex = this.panelStates[slot]?.videoTexture
    // Still worth the immediate hint: a seek that needs no data never
    // fires `seeked`, and this is the only upload it will get.
    if (tex) tex.needsUpdate = true
    if (!video.seeking || this.siblingSeekUploads[slot]) return

    const armed = new AbortController()
    this.siblingSeekUploads[slot] = armed
    video.addEventListener('seeked', () => {
      this.siblingSeekUploads[slot] = null
      // Looked up now rather than captured, so a dataset swap between
      // arming and firing retargets this instead of writing through a
      // handle that no longer belongs to the panel.
      const current = this.panelStates[slot]?.videoTexture
      if (current) current.needsUpdate = true
    }, { once: true, signal: armed.signal })
  }

  /**
   * Drop any armed upload for a slot, or for every slot.
   *
   * Called wherever a panel's video goes away. `seeked` never fires on a
   * destroyed element, so without this the slot stays marked armed
   * forever and refuses to arm again — the tour swaps sibling datasets a
   * dozen times, so the fix would quietly stop working panel by panel.
   */
  private cancelSiblingSeekUpload(slot?: number): void {
    if (slot === undefined) {
      for (const armed of this.siblingSeekUploads) armed?.abort()
      this.siblingSeekUploads = []
      if (this.siblingRepairTimer !== null) {
        clearTimeout(this.siblingRepairTimer)
        this.siblingRepairTimer = null
      }
      return
    }
    this.siblingSeekUploads[slot]?.abort()
    this.siblingSeekUploads[slot] = null
  }

  /**
   * Read back whether every sibling actually shows the date the shared
   * time label claims, and mark the ones that don't.
   *
   * The multi-globe layout asserts one label over every panel, derived
   * from the primary alone. Siblings are commanded to that date by
   * `seekSiblingsToDate` and never read back, so a write that fails to
   * land leaves the label quietly speaking for a panel that is somewhere
   * else. `correctSiblingDrift` would notice, but it returns early while
   * the primary is paused — and paused is exactly when a viewer reads
   * the label and compares panels.
   *
   * Runs on settle rather than per frame: the playback settle watcher
   * fires once the transport is paused, not seeking, and still, which is
   * both the only moment the answer is stable and the only moment it
   * matters. Verification only — correcting here would fight the sync
   * controller for the playhead, and a panel that cannot reach the
   * labelled moment should say so rather than silently retry.
   */
  private verifySiblingTimes(afterRepair = false): void {
    if (this.siblingRepairTimer !== null) {
      clearTimeout(this.siblingRepairTimer)
      this.siblingRepairTimer = null
    }
    // A re-check that arrives after the transport moved again describes
    // nothing; the moving-clear path owns the notices from here.
    const primaryVideo = this.panelStates[this.viewports.getPrimaryIndex()]?.hlsService?.getVideo?.() ?? null
    if (afterRepair && (!primaryVideo || !primaryVideo.paused || primaryVideo.seeking)) return

    let repairing = false
    const labelDate = this.assertedLabelDate
    // Nothing is being asserted (no dataset, or no temporal range), so
    // there is no claim for a panel to contradict.
    if (!labelDate) {
      this.clearSiblingTimeNotices()
      return
    }

    const pIdx = this.viewports.getPrimaryIndex()
    const snapMs = this.playback.displayInterval?.intervalMs

    for (let i = 0; i < this.panelStates.length; i++) {
      if (i === pIdx) continue
      const panel = this.panelStates[i]
      const video = panel?.hlsService?.getVideo?.() ?? null
      const renderer = this.viewports.getRendererAt(i)
      const ds = panel?.dataset

      // No video, no range, or a duration we cannot map through: there
      // is nothing to compare, which is not the same as a mismatch.
      if (!video || !ds?.startTime || !ds.endTime || !(video.duration > 0)) {
        this.setSiblingTimeNotice(i, null)
        continue
      }

      const verdict = verifySiblingTime({
        labelDate,
        // What the globe is showing, not what its video element says —
        // see `shownFrameTime`. The renderer knows which frame actually
        // reached the texture; the element only knows its own clock.
        sibFrameTime: shownFrameTime(
          renderer instanceof MapRenderer ? renderer.getUploadedFrameTime() : null,
          video.currentTime,
        ),
        sibDuration: video.duration,
        sibStart: new Date(ds.startTime),
        sibEnd: new Date(ds.endTime),
        snapIntervalMs: snapMs,
      })

      // `uncovered` is left alone deliberately — the out-of-range
      // treatment already explains that panel, and saying it twice in
      // two different vocabularies is worse than saying it once.
      if (verdict.alignment !== 'off') {
        this.setSiblingTimeNotice(i, null)
        continue
      }

      // The panel is showing the wrong frame. Before saying so, ask for
      // a redraw — the overwhelmingly common cause is a texture left
      // behind a clock that is already correct, and one upload fixes
      // that whatever produced it. This is not the correction
      // `verifySiblingTime`'s contract rules out: that is about seeking,
      // which would fight the sync controller for the playhead. Nothing
      // here moves a playhead; it repaints what the video already holds.
      const tex = this.panelStates[i]?.videoTexture
      if (!afterRepair && tex) {
        tex.needsUpdate = true
        repairing = true
        continue
      }

      const showTime = ds.period
        ? isSubDailyPeriod(ds.period)
        : (this.playback.displayInterval?.showTime ?? false)
      this.setSiblingTimeNotice(i, formatDate(verdict.shownDate, showTime))
      logger.debug(
        `[App] Panel ${i} is ${Math.round(verdict.driftMs / 1000)}s of real time off the label`
        + (afterRepair ? ' and did not repaint' : ''),
      )
    }

    // Confirm the repair rather than assuming it. A notice is only
    // earned by a panel that stayed wrong after being asked to redraw,
    // which keeps the ribbon meaning "I could not fix this" instead of
    // flashing up for every transient staleness.
    if (repairing) {
      this.siblingRepairTimer = setTimeout(() => {
        this.siblingRepairTimer = null
        this.verifySiblingTimes(true)
      }, SIBLING_REPAIR_CONFIRM_MS)
    }
  }

  /** Show or hide one panel's time notice, tracking it for cheap clearing. */
  private setSiblingTimeNotice(slot: number, shownDate: string | null): void {
    this.viewports.setPanelTimeNotice(slot, shownDate)
    this.siblingTimeNoticed[slot] = shownDate !== null
  }

  /**
   * Drop every time notice.
   *
   * A notice describes one settled position, so it goes stale the
   * instant the transport moves again. The array scan short-circuits
   * when nothing is marked, which is the overwhelmingly common case.
   */
  /**
   * Drop stale time notices once the transport is moving again.
   *
   * "Moving" is playing *or* seeking. A scrub while paused leaves
   * `paused` true and only raises `seeking`, so testing `paused` alone
   * held the previous notice on screen for the whole drag — naming one
   * date while the label and the panels moved through others, which is
   * the opposite of what a notice about a settled frame should mean.
   *
   * Called from the playback loop, which ticks whether or not the
   * primary is playing, so it makes its own check. Both guards are
   * cheap and short-circuit before touching the DOM.
   */
  private clearSiblingTimeNoticesWhileMoving(): void {
    if (!this.siblingTimeNoticed.some(Boolean)) return
    const primary = this.panelStates[this.viewports.getPrimaryIndex()]?.hlsService?.getVideo?.() ?? null
    if (!primary) return
    if (primary.paused && !primary.seeking) return
    this.clearSiblingTimeNotices()
  }

  private clearSiblingTimeNotices(): void {
    if (!this.siblingTimeNoticed.some(Boolean)) return
    for (let i = 0; i < this.siblingTimeNoticed.length; i++) {
      if (this.siblingTimeNoticed[i]) this.setSiblingTimeNotice(i, null)
    }
  }

  /**
   * Detach all sibling-sync listeners from the previous primary video,
   * disarm per-frame drift correction, and clear any lingering
   * out-of-range or time-notice state from siblings.
   */
  private detachPrimaryVideoSync(): void {
    this.primaryVideoSyncActive = false
    this.siblingOutOfRange = []
    // A time notice names a date for a specific panel showing a specific
    // dataset. Both can change out from under it here — a layout change,
    // a new primary — so it is cleared rather than left to be corrected
    // on the next settle.
    this.clearSiblingTimeNotices()
    this.siblingTimeNoticed = []
    this.cancelSiblingSeekUpload()
    // And the settle detector has to forget where it was, for the reason
    // its own docs give: it reports a position once, so a new primary
    // paused at the same `currentTime` — the normal case for
    // equal-duration videos held in lockstep — would be suppressed as
    // already-reported and never verified. The playhead is the same
    // number but a different panel's frame.
    this.playbackSettle.reset()
    const target = this.primaryVideoSyncTarget
    if (target) {
      for (const { event, handler } of this.primaryVideoSyncListeners) {
        target.removeEventListener(event, handler)
      }
    }
    this.primaryVideoSyncListeners = []
    this.primaryVideoSyncTarget = null
    // Reset sibling playback rates to 1.0 so videos don't stay at
    // the adjusted speed after sync is torn down.
    for (let i = 0; i < this.panelStates.length; i++) {
      this.viewports.setOutOfRange(i, false)
      const sibVideo = this.panelStates[i]?.hlsService?.getVideo?.() ?? null
      if (sibVideo) sibVideo.playbackRate = 1.0
    }
  }

  /**
   * Dispose the video texture and HLS service for a specific panel.
   * If `slot` is omitted, operates on the primary panel (common case
   * for single-viewport flows like goHome / loadDataset teardown).
   *
   * When tearing down the primary, also resets the shared playback
   * state; non-primary slots don't touch that state.
   */
  private cleanupPanelVideo(slot?: number): void {
    const targetSlot = slot ?? this.viewports.getPrimaryIndex()
    const panel = this.panelStates[targetSlot]
    if (!panel) return

    // Before anything else: this slot's video is about to be destroyed,
    // and an armed listener on it would never fire.
    this.cancelSiblingSeekUpload(targetSlot)

    const isPrimary = targetSlot === this.viewports.getPrimaryIndex()
    if (isPrimary) {
      this.detachPrimaryVideoSync()
      stopPlaybackLoop(this.playback)
    }

    if (panel.videoTexture) {
      panel.videoTexture.dispose()
      panel.videoTexture = null
    }
    if (panel.hlsService) {
      panel.hlsService.destroy()
      panel.hlsService = null
    }
    // The notice described a stream that no longer exists. `destroy()`
    // above already dropped the handler, so nothing can re-raise it.
    this.viewports.setPanelStreamNotice(targetSlot, false)

    if (isPrimary) {
      this.appState.isPlaying = false
      resetPlaybackState(this.playback)
    }
  }

  /**
   * Legacy alias for `cleanupPanelVideo(primarySlot)`. Kept so
   * existing call sites that mean "clean up the video that's
   * currently playing" stay readable.
   */
  private cleanupVideo(): void {
    this.cleanupPanelVideo()
  }

  /**
   * Tear down datasets in every panel — used by goHome and unloadForTour.
   * Clears dataset + hls + video texture per slot and resets shared
   * Delegates to the per-panel unload path so every slot gets the
   * same dataset/video teardown AND renderer reset (back to default
   * Earth materials) behavior.
   */
  private async unloadAllPanels(): Promise<void> {
    this.detachPrimaryVideoSync()
    stopPlaybackLoop(this.playback)
    this.appState.isPlaying = false
    resetPlaybackState(this.playback)
    for (let slot = 0; slot < this.panelStates.length; slot++) {
      await this.unloadPanelDataset(slot)
    }
    this.infoDisplayOverride = null
    this.refreshPanelLegends()
    this.renderInfoPanel()
  }

  /**
   * Unload a single panel's dataset — video, texture, metadata.
   * Called by the tour engine's `unloadDataset` task after it
   * resolves a local `datasetID` handle to a slot. Keeps the rest
   * of the panels intact, unlike `unloadAllPanels`.
   *
   * When the target slot is the primary, also tears down the shared
   * playback state. Non-primary slots leave the playback UI alone.
   */
  private async unloadPanelDataset(slot: number): Promise<void> {
    const panel = this.panelStates[slot]
    if (!panel) return
    const isPrimarySlot = slot === this.viewports.getPrimaryIndex()

    // Clean the video stream, texture, and (if primary) the shared
    // playback state. cleanupPanelVideo handles both.
    this.cleanupPanelVideo(slot)

    // Clear the dataset reference and reset the base earth on that
    // specific panel's renderer so it doesn't keep showing a stale
    // texture.
    panel.dataset = null
    panel.image = null
    panel.mediaDatasetId = null
    // After the clear, not before: `emitLayerUnloadedForSlot` runs while
    // the panel still holds the outgoing row (it reports on it), so
    // publishing there would restate the dataset being removed.
    this.publishMirroredDataset()
    const renderer = this.viewports.getRendererAt(slot)
    if (renderer instanceof MapRenderer) {
      // Drop the panel's dataset-credits phantom source so Tools
      // → Credits no longer surfaces a stale attribution.
      renderer.setDatasetCredits(null)
    }
    if (renderer) {
      renderer.removeCloudOverlay?.()
      renderer.removeNightLights?.()
      renderer.disableSunLighting?.()
      try {
        await renderer.loadDefaultEarthMaterials?.()
        const sun = getSunPosition(new Date())
        renderer.enableSunLighting?.(sun.lat, sun.lng)
      } catch (err) {
        logger.warn('[App] Earth material reload after unload failed:', err)
      }
    }

    // If we just cleared the currently-displayed slot in the info
    // panel, drop the override so renderInfoPanel picks a new target.
    if (this.infoDisplayOverride === slot) {
      this.infoDisplayOverride = null
    }

    // Reflect the clear in the primary UI if applicable.
    if (isPrimarySlot) {
      this.appState.currentDataset = null
    }

    this.refreshPanelLegends()
    this.renderInfoPanel()
  }

  // --- Event listeners ---

  /** Wire up all DOM event listeners: transport controls, keyboard shortcuts, scrubber, mute. */
  setupEventListeners(): void {
    // Back/forward cache restore (iOS Safari in particular). MapLibre
    // caches its canvas dimensions; after a bfcache restore the canvas
    // can be stuck at a stale size and the globe renders in a thin
    // strip. `persisted === true` only fires for bfcache restores, so
    // this is cheap on every other navigation.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.viewports.resizeAll()
    })

    document.getElementById('home-btn')?.addEventListener('click', () => this.goHome())

    // Browse panel opens via the Tools menu's Browse button (see
    // openBrowsePanel). No standalone peek-out toggle tab.

    // Transport controls — delegate to playback module
    document.getElementById('rewind-btn')?.addEventListener('click', () => {
      rewind(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    document.getElementById('step-back-btn')?.addEventListener('click', () => {
      stepFrame(-1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    document.getElementById('play-btn')?.addEventListener('click', () => {
      const wasPaused = this.hlsService?.paused ?? true
      togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m))
      this.emitPlaybackAction(wasPaused ? 'play' : 'pause')
    })
    document.getElementById('step-fwd-btn')?.addEventListener('click', () => {
      stepFrame(1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    document.getElementById('ff-btn')?.addEventListener('click', () => {
      fastForward(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      this.emitPlaybackAction('seek')
    })
    // "Next in playlist" — only visible while a playlist is active
    // (see the onPlaybackChange wiring in init above). Bypasses the
    // per-entry timer and the tour-deferral wait alike.
    document.getElementById('playlist-next-btn')?.addEventListener('click', () => {
      skipNextPlaylistEntry()
    })
    document.getElementById('cc-btn')?.addEventListener('click', () =>
      toggleCaptions(this.playback))

    // Mute/unmute toggle
    const muteBtn = document.getElementById('mute-btn')
    if (muteBtn) {
      muteBtn.addEventListener('click', () => {
        const video = this.hlsService?.video
        if (!video) return
        video.muted = !video.muted
        muteBtn.textContent = video.muted ? '\u{1F507}\uFE0E' : '\u{1F50A}\uFE0E'
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute audio' : 'Mute audio')
        muteBtn.style.color = video.muted ? '#aaa' : '#4da6ff'
        muteBtn.style.borderColor = video.muted ? '#555' : '#4da6ff'
      })
    }

    // Auto-rotate lives inside the Tools menu now — see toolsMenuUI.ts.

    // Scrubber
    const scrubber = document.getElementById('scrubber') as HTMLInputElement
    if (scrubber) {
      scrubber.addEventListener('input', () => {
        onScrub(parseInt(scrubber.value, 10), this.hlsService, this.playback)
      })
    }

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      const browseOverlay = document.getElementById('browse-overlay')
      if (browseOverlay && !browseOverlay.classList.contains('hidden') && browseOverlay.contains(e.target as Node)) return

      if (e.code === 'Space') {
        e.preventDefault()
        togglePlayPause(this.hlsService, this.appState, (m) => this.announce(m))
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault()
        stepFrame(-1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      } else if (e.code === 'ArrowRight') {
        e.preventDefault()
        stepFrame(1, this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      } else if (e.code === 'Home') {
        e.preventDefault()
        rewind(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      } else if (e.code === 'End') {
        e.preventDefault()
        fastForward(this.hlsService, this.appState, this.playback, (m) => this.announce(m))
      }
    })
  }

  // --- Navigation ---

  /** Load a dataset selected via the browse panel, updating URL and shifting focus to playback controls. */
  private async selectDatasetFromBrowse(id: string): Promise<void> {
    const gen = ++this.loadGeneration
    this.dismissBrowseAfterLoad()
    closeChat()
    this.announce('Loading dataset\u2026')
    this.showLoadingScreen('Loading dataset\u2026', 20)
    // `writeDatasetUrl` carries the existing query across, which
    // preserves `?catalog=true` so the catalog↔sphere tab control
    // (Phase 1 §3.2) can offer a "back to catalog" affordance. The
    // body class stays set so CSS continues to recognise the
    // catalog-mode surface; the regular load flow swaps the visible
    // browse panel for the globe via `dismissBrowseAfterLoad()`.
    // Read `getCatalogMode()` before the write so the URL state we
    // observe and the tab state we update agree by construction.
    const inCatalogMode = getCatalogMode()
    this.writeDatasetUrl(id, 'push')
    // The globe is now the active surface — drop the
    // `catalog-empty` flag so CSS reveals `#map-grid`. The
    // `catalog-mode` body class stays (sticky session marker).
    // Flip the tab control to Sphere since the globe is what the
    // visitor is now looking at.
    document.body.classList.remove('catalog-empty')
    if (inCatalogMode) setActiveCatalogTab('sphere')
    await this.loadDataset(id, 'orbit')
    if (gen !== this.loadGeneration) return // a newer load superseded this one
    this.setLoading(false)
    showChatTrigger()
    const dataset = this.appState.currentDataset
    if (dataset) {
      this.announce(`Loaded dataset: ${dataset.title}`)
      this.renderer?.setCanvasDescription(`3D globe showing ${dataset.title}`)
      notifyDatasetChanged(dataset)
      setHelpActiveDataset(dataset.id)
    }
    const playBtn = document.getElementById('play-btn')
    const infoHeader = document.getElementById('info-header')
    if (playBtn && !playBtn.closest('.hidden')) {
      playBtn.focus()
    } else if (infoHeader) {
      infoHeader.focus()
    }
  }

  /** Re-show the loading screen with a fade-in if it was hidden, cancelling any pending hide timer. */
  private showLoadingScreen(message = 'Loading dataset\u2026', progress = 0): void {
    if (this.loadingHideTimer !== null) {
      clearTimeout(this.loadingHideTimer)
      this.loadingHideTimer = null
    }
    const screen = document.getElementById('loading-screen')
    if (!screen) return
    screen.classList.remove('fade-out')

    const wasHidden = screen.style.display === 'none'
    screen.style.display = 'flex'
    if (wasHidden) {
      screen.style.opacity = '0'
      void screen.offsetHeight
      screen.style.transition = 'opacity 0.3s ease'
      screen.style.opacity = '1'
      // The screen's own fade only, as in `setLoading`.
      const onShown = (e: TransitionEvent): void => {
        if (e.target !== screen) return
        screen.removeEventListener('transitionend', onShown)
        screen.style.transition = ''
        screen.style.opacity = ''
      }
      screen.addEventListener('transitionend', onShown)
    } else {
      screen.style.opacity = ''
    }
    this.setLoadingStatus(message, progress)
  }

  /** Show the home navigation button. */
  private showHomeButton(): void {
    document.getElementById('home-btn')?.classList.remove('hidden')
  }

  /** Hide the home navigation button. */
  private hideHomeButton(): void {
    document.getElementById('home-btn')?.classList.add('hidden')
  }

  /** Navigate back to the default Earth view: tear down every panel's dataset, reload Earth materials, and re-show the browse panel. */
  private async goHome(): Promise<void> {
    this.stopTour()
    // Emit layer_unloaded for every panel that currently has a
    // dataset so dwell_ms can be accounted for. Runs before
    // unloadAllPanels() so panelStates[*].dataset is still populated.
    for (let i = 0; i < this.panelStates.length; i++) {
      this.emitLayerUnloadedForSlot(i, 'home')
    }
    await this.unloadAllPanels()
    clearLegendCache()
    this.appState.currentDataset = null
    this.showPlaybackControls(false)
    this.showTimeLabel(false)
    document.getElementById('info-panel')?.classList.add('hidden')
    this.hideHomeButton()
    // Reset overlays that tours may have turned on. In multi-view
    // mode we fan out across every panel so siblings don't keep
    // stale labels/borders after home.
    for (const r of this.viewports.getAll()) {
      r.toggleLabels?.(false)
      r.toggleBoundaries?.(false)
    }
    syncToolsMenuState({ labels: false, borders: false, terrain: false, autoRotate: false })
    this.clearDatasetUrl('push')

    this.showLoadingScreen('Loading Earth\u2026', 20)
    if (this.renderer) {
      const cloudUrl = CLOUD_TEXTURE_URL
      let earthFraction = 0
      let cloudFraction = 0
      const updateProgress = () => {
        const combined = earthFraction * EARTH_TEXTURE_WEIGHT + cloudFraction * CLOUD_TEXTURE_WEIGHT
        this.setLoadingStatus('Loading Earth\u2026', LOADING_BASE_PROGRESS + Math.round(combined * LOADING_TEXTURE_RANGE))
      }
      await Promise.all([
        this.renderer.loadDefaultEarthMaterials((f: number) => { earthFraction = f; updateProgress() }),
        this.renderer.loadCloudOverlay(cloudUrl, (f: number) => { cloudFraction = f; updateProgress() })
      ])
      const sun = getSunPosition(new Date())
      this.renderer.enableSunLighting(sun.lat, sun.lng)
    }
    this.setLoading(false)
    hideChatTrigger()
    closeChat()
    showBrowseUI(this.appState.datasets, {
      onSelectDataset: (id) => this.selectDatasetFromBrowse(id),
      announce: (msg) => this.announce(msg),
      isMobile: this.isMobile,
      onOpenChat: this.browseOpenChat(),
    })
    this.renderer?.setCanvasDescription('Interactive 3D globe showing Earth')
    notifyDatasetChanged(null)
    setHelpActiveDataset(null)
  }

  /**
   * Load a tour from a URL — exposed for console testing.
   * Usage: window.app.playTour('/assets/test-tour.json')
   */
  async playTour(url: string): Promise<void> {
    const gen = ++this.loadGeneration
    this.tourIsStandalone = true
    await this.startTour(url, gen)
  }

  /**
   * Synchronously release video/HLS resources across all panels
   * without the async Earth-material reload that `unloadAllPanels`
   * performs. Used by `dispose()` where the renderers are about to
   * be torn down and awaiting async work is neither necessary nor
   * safe.
   */
  private teardownAllPanelResources(): void {
    this.detachPrimaryVideoSync()
    stopPlaybackLoop(this.playback)
    this.appState.isPlaying = false
    resetPlaybackState(this.playback)
    for (let i = 0; i < this.panelStates.length; i++) {
      const panel = this.panelStates[i]
      if (panel.videoTexture) { panel.videoTexture.dispose(); panel.videoTexture = null }
      if (panel.hlsService) { panel.hlsService.destroy(); panel.hlsService = null }
      panel.dataset = null
      panel.image = null
      panel.mediaDatasetId = null
      const renderer = this.viewports.getRendererAt(i)
      if (renderer instanceof MapRenderer) {
        renderer.setDatasetCredits(null)
      }
    }
  }

  /** Clean up all resources: video streams, textures, and every viewport renderer. */
  /**
   * Fullscreen, F11 and the idle cursor for the control window (§3.6).
   *
   * All of this exists because a title bar leaks into the signal: the
   * common installation captures a monitor over HDMI, so the window's
   * own chrome arrives on the sphere with the picture.
   *
   * The persisted state is restored **only on desktop**, and that is
   * not a tidiness rule — `requestFullscreen` needs a user gesture, so
   * restoring on the web throws on every launch and changes nothing.
   * `restoreOnLaunch` holds both halves of that test.
   */
  private initWindowChrome(): void {
    const host = resolveChromeHost()
    const fullscreen = createFullscreenController({ host, persist: true })
    // Ctrl+Q, the control window only. A kiosk launch leaves no close
    // button, no title bar and no menu bar, so without this the only
    // way out on Linux is a window-manager binding that may not exist.
    // Inert on the web by construction: the DOM host implements no
    // `quit`, which is what stops this swallowing Firefox's own Ctrl+Q.
    createQuitHotkey({ host })
    const idleCursor = createIdleCursor()
    // Only while fullscreen: hiding the pointer of a windowed app the
    // operator is still driving would be a bug, not a feature.
    fullscreen.onChange(on => idleCursor.setActive(on))
    this.fullscreen = fullscreen
    this.idleCursor = idleCursor

    if (restoreOnLaunch()) {
      void fullscreen.set(true).catch(err => {
        // Costs the restored state, never the boot that was applying it.
        logger.warn('[Main] could not restore fullscreen:', err)
      })
    }
  }

  dispose(): void {
    // Before the handle goes: the panel holds a document-level keydown
    // listener, and it reads through `this.multiOutput`.
    closeOutputUI()
    this.multiOutput?.stop()
    this.multiOutput = null
    this.unbindOperatorCamera?.()
    this.unbindOperatorCamera = null
    this.fullscreen?.dispose()
    this.fullscreen = null
    this.idleCursor?.dispose()
    this.idleCursor = null
    this.teardownAllPanelResources()
    this.viewports.dispose()
    this.panelStates = []
  }
}

// Register service worker for tile caching (cache-first strategy for GIBS tiles).
// Skip in Tauri desktop app — tile caching is handled by the Rust backend.
if ('serviceWorker' in navigator && !(window as any).__TAURI__) {
  navigator.serviceWorker.register('/sw.js').catch(err => {
    logger.warn('[SW] Registration failed:', err)
  })
}

// Check for app updates on launch (Tauri desktop only).
// Runs in the background after the app is fully loaded — non-blocking.
async function checkForUpdates(): Promise<void> {
  if (!(window as any).__TAURI__) return
  try {
    const { check } = await import('@tauri-apps/plugin-updater')
    const update = await check()
    if (update) {
      logger.info(`[Updater] Update available: ${update.version}`)
      // download + install; the plugin shows a native confirmation dialog
      // because "dialog: true" is set in tauri.conf.json
      await update.downloadAndInstall()
    }
  } catch (err) {
    logger.warn('[Updater] Update check failed:', err)
  }
}

// Initialize app on DOM ready
document.addEventListener('DOMContentLoaded', async () => {
  const isBlogPath = location.pathname === '/blog' || location.pathname.startsWith('/blog/')
  const isGlobeApp = !isPublisherPortalPath(location.pathname) && !isBlogPath

  // The node profile read, started before the i18n bootstrap: it needs
  // no locale, and the splash is already on screen waiting for its
  // tagline (src/ui/splashTagline.ts). Started any later — after a
  // non-English visitor's locale chunk — a cold load would outlast the
  // splash's reveal deadline and swap the node's line in over the
  // default, the flash the hold-back exists to prevent. The same read
  // carries the feature toggles `initialize` waits on. Not for the
  // portal or blog, which read the profile themselves.
  const nodeFeatures = isGlobeApp ? startNodeFeaturesRead() : null

  // Resolve user locale and apply data-i18n attributes before any
  // UI module renders. English is bundled inline; non-English
  // locales lazy-load here. See docs/I18N_PLAN.md.
  const i18nReady = bootstrapI18n()
  if (nodeFeatures) {
    // The default is revealed only once it is translated, so a visitor
    // in another locale never sees the English line first; the node's
    // own line, shown as written, does not wait.
    void bindSplashTagline(document.querySelector<HTMLElement>('.loading-subtitle'), nodeFeatures.tagline, {
      revealAfterMs: SPLASH_TAGLINE_WAIT_MS,
      defaultReady: i18nReady,
    })
    // The browse panel's subtitle rides the same read. It needs no
    // hold-back because `initialize` awaits this read's `settle()`
    // before any path reveals the browse panel (see browseSubtitle.ts):
    // a node's own line is written in when the read answers.
    void bindBrowseSubtitle(document.querySelector<HTMLElement>('.browse-subtitle'), nodeFeatures.browseSubtitle)
  }
  await i18nReady

  // Publisher portal route gate. Visits to `/publish/*` boot a
  // separate admin UI (Cloudflare Access-protected in production)
  // and skip the SPA's globe / dataset / chat boot entirely. The
  // portal chunk is fetched only when this branch fires, so
  // non-publisher visitors never pay its bundle cost. See
  // docs/CATALOG_PUBLISHING_TOOLS.md §"Phase 3 implementation
  // conventions" → "Lazy-load shape". The predicate is shared with
  // the `_headers` test, so the portal only boots on paths the
  // framing rules protect.
  if (isPublisherPortalPath(location.pathname)) {
    const { bootPublisherPortal } = await import('./ui/publisher')
    await bootPublisherPortal()
    return
  }

  // Public blog route gate — same lazy-chunk shape as the portal:
  // `/blog` and `/blog/:slug` render static content pages and skip
  // the globe boot entirely (docs/CURRENT_EVENTS_PLAN.md §7).
  if (isBlogPath) {
    const { bootBlogPage } = await import('./ui/blog')
    await bootBlogPage()
    return
  }

  const app = new InteractiveSphere()
  app.setupEventListeners()
  await app.initialize(nodeFeatures ?? undefined)

  ;(window as any).app = app

  // Emit session_start after the app is fully initialized so every
  // subsequent event carries a non-zero client_offset_ms relative to
  // it. The hook itself is best-effort — if telemetry is off (tier
  // or compile flag) the emit() calls inside no-op.
  if (TELEMETRY_BUILD_ENABLED) {
    void initSession()
    startPerfSampler()
  }

  // §9.2 — stamp the session-end timestamp on pagehide so the next
  // visit can compute "new since your last visit" and (Phase 7 §9.3)
  // the returning-user Orbit greeting trigger. This is a synchronous
  // localStorage write, which is permitted from `pagehide` (unlike
  // sendBeacon); it rides alongside the analytics session_end flush
  // but is independent of the telemetry build flag — visit memory is
  // a local convenience cache, never sent server-side.
  window.addEventListener('pagehide', () => writeLastSession())

  // Non-blocking update check after app is ready
  checkForUpdates()
})
